/**
 * Editable Renderer - Visual Template Editor Core
 *
 * Fork of StructureRenderer.tsx for the designer portal.
 * Renders template JSON visually with inline editing affordances:
 * - Click text to edit inline
 * - Click images to replace
 * - Section wrappers with controls
 * - Proper mobile/desktop preview (strips responsive Tailwind prefixes)
 * - Theme CSS variables for live theme switching
 */

import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import {
  Search, Heart, ShoppingCart, User, Menu, X,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Star, MapPin, Phone, Mail, Facebook, Instagram, Twitter,
  Youtube, Linkedin, ArrowRight, ArrowLeft, ArrowUp, ArrowDown,
  Play, Pause, Home, Package, Settings, LogOut,
  Calendar, Clock, Check, AlertCircle, Info, XCircle, CheckCircle,
  Plus, Minus, Edit, Trash2, Eye, EyeOff, Filter,
  Download, Upload, Share2, Link2, Image as ImageIcon, Video, Music,
  File, Folder, Send, MessageCircle, Bell, Globe,
  RefreshCw, TrendingUp, Award, Gift, Tag, Bookmark,
  Layers, Grid, List, Layout, Type, Quote, Truck, Shield,
  ShoppingBag, CreditCard, Lock, Wallet,
  BadgeCheck, Store, Building,
  HelpCircle, FileText, BookOpen,
  ShieldCheck, AlertTriangle, MessageSquare, Headphones, LifeBuoy,
  Sparkles, Flame, Loader2, Maximize, Minimize, Move,
  ExternalLink, Copy, Smartphone, Tablet, Monitor,
  Briefcase, PenTool, Palette, Paintbrush, Scissors, Ruler,
  Gem, Zap, Sun, Moon, Camera, Mic, Receipt, Banknote,
  CircleDollarSign, DollarSign, Percent, BarChart,
  Flag, Anchor, Compass, Target, Crosshair,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic, Underline,
  AtSign, Hash, Key, Unlock,
  Newspaper, GraduationCap, Library,
  Shapes, Triangle, Square, Circle, Hexagon, Pentagon,
  GripVertical, PlusCircle
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  resolveDataPath, resolveBindings, extractBindingPath,
  isEditableText, isEditableImage, isEditableBackgroundImage,
  extractBackgroundImageUrl, hasBindings, evaluateExpression
} from './bindingUtils';
import NavbarPreview from './previews/NavbarPreview';
import FooterPreview from './previews/FooterPreview';
import ShopProductGridPreview from './previews/ShopProductGridPreview';
import ProductDetailPreview from './previews/ProductDetailPreview';
import LoginFormPreview from './previews/LoginFormPreview';
import ContactFormPreview from './previews/ContactFormPreview';
import CartPreview from './previews/CartPreview';
import CheckoutPreview from './previews/CheckoutPreview';

// ============================================================================
// THEME COLOR CSS — maps Tailwind color classes to CSS variables
// Static rules: only the CSS variable values change (via themeStyle).
// This enables theme color changes to visually apply across all templates
// regardless of whether the template JSON uses hardcoded Tailwind classes.
// ============================================================================

const THEME_COLOR_CSS = (() => {
  const s = '.editable-renderer';
  const rules = [];

  const neutrals = ['gray', 'slate', 'neutral', 'zinc', 'stone'];
  for (const f of neutrals) {
    // Dark backgrounds → primary
    rules.push(`${s} .bg-${f}-900,${s} .bg-${f}-950{background-color:var(--color-primary)!important}`);
    rules.push(`${s} .bg-${f}-800{background-color:var(--color-primary-light,var(--color-primary))!important}`);
    // Light backgrounds → surface (matches customer-side themeUtils: bg-gray-50/100 → surface)
    rules.push(`${s} .bg-${f}-50{background-color:var(--color-surface,#F9FAFB)!important}`);
    rules.push(`${s} .bg-${f}-100{background-color:var(--color-surface,#F3F4F6)!important}`);
    // Text dark → text
    rules.push(`${s} .text-${f}-900,${s} .text-${f}-950,${s} .text-${f}-800{color:var(--color-text)!important}`);
    // Text medium → textSecondary
    rules.push(`${s} .text-${f}-600,${s} .text-${f}-700,${s} .text-${f}-500{color:var(--color-text-secondary,var(--color-text))!important}`);
    // Text light → textLight
    rules.push(`${s} .text-${f}-400,${s} .text-${f}-300{color:var(--color-text-light,#9CA3AF)!important}`);
    // Borders
    rules.push(`${s} .border-${f}-200,${s} .border-${f}-300{border-color:var(--color-border)!important}`);
    rules.push(`${s} .border-${f}-800,${s} .border-${f}-900{border-color:var(--color-primary)!important}`);
    // Hover states
    rules.push(`${s} .hover\\:bg-${f}-800:hover,${s} .hover\\:bg-${f}-700:hover{background-color:var(--color-primary-light,var(--color-primary))!important}`);
    // Focus borders
    rules.push(`${s} .focus\\:border-${f}-900:focus{border-color:var(--color-primary)!important}`);
  }

  // Surface (white)
  rules.push(`${s} .bg-white{background-color:var(--color-surface,#fff)!important}`);

  // Black backgrounds & text → map to theme colors
  rules.push(`${s} .bg-black{background-color:var(--color-primary)!important}`);
  rules.push(`${s} .text-black{color:var(--color-text,#000)!important}`);
  rules.push(`${s} .text-white{color:var(--color-text-on-primary,#fff)!important}`);
  rules.push(`${s} .hover\\:bg-black:hover{background-color:var(--color-primary-light,var(--color-primary))!important}`);
  rules.push(`${s} .hover\\:text-black:hover{color:var(--color-text,#000)!important}`);
  rules.push(`${s} .hover\\:text-white:hover{color:var(--color-text-on-primary,#fff)!important}`);
  rules.push(`${s} .border-black{border-color:var(--color-primary)!important}`);
  rules.push(`${s} .border-white{border-color:var(--color-surface,#fff)!important}`);

  // Accent color families → accent CSS variable (falls back to primary)
  const accents = ['amber', 'rose', 'orange', 'red', 'emerald', 'teal', 'blue', 'indigo', 'violet', 'purple', 'pink'];
  for (const f of accents) {
    rules.push(`${s} .text-${f}-950,${s} .text-${f}-900{color:var(--color-accent,var(--color-primary))!important}`);
    rules.push(`${s} .text-${f}-700,${s} .text-${f}-800{color:var(--color-accent-light,var(--color-accent,var(--color-primary)))!important}`);
    rules.push(`${s} .bg-${f}-900,${s} .bg-${f}-950{background-color:var(--color-accent,var(--color-primary))!important}`);
    rules.push(`${s} .bg-${f}-700,${s} .bg-${f}-800{background-color:var(--color-accent-light,var(--color-accent,var(--color-primary)))!important}`);
    rules.push(`${s} .border-${f}-900,${s} .border-${f}-800,${s} .border-${f}-700{border-color:var(--color-accent,var(--color-primary))!important}`);
    rules.push(`${s} .hover\\:border-${f}-700:hover{border-color:var(--color-accent-light,var(--color-accent,var(--color-primary)))!important}`);
    rules.push(`${s} .hover\\:bg-${f}-700:hover,${s} .hover\\:bg-${f}-800:hover{background-color:var(--color-accent-light,var(--color-accent,var(--color-primary)))!important}`);
  }

  // Gradient from/to stops for neutral darks → primary
  for (const f of neutrals) {
    rules.push(`${s} .from-${f}-900\\/60{--tw-gradient-from:color-mix(in srgb,var(--color-primary) 60%,transparent)!important}`);
  }

  // Focus ring/border states (matches customer-side getThemeGlobalCSS)
  for (const f of neutrals) {
    rules.push(`${s} .focus\\:border-${f}-900:focus,${s} .focus\\:border-${f}-800:focus{border-color:var(--color-primary)!important}`);
    rules.push(`${s} .focus\\:ring-${f}-900:focus,${s} .focus\\:ring-${f}-800:focus{--tw-ring-color:var(--color-primary)!important}`);
    rules.push(`${s} .hover\\:bg-${f}-50:hover,${s} .hover\\:bg-${f}-100:hover{background-color:var(--color-surface,#F9FAFB)!important}`);
    rules.push(`${s} .hover\\:text-${f}-900:hover{color:var(--color-text)!important}`);
  }

  // Checkbox accent color (matches customer-side getThemeGlobalCSS)
  rules.push(`${s} input[type="checkbox"]{accent-color:var(--color-primary)}`);

  // Font family application — apply CSS variables to all content
  rules.push(`${s} h1,${s} h2,${s} h3,${s} h4,${s} h5,${s} h6{font-family:var(--font-heading,inherit)!important}`);
  rules.push(`${s} p,${s} span,${s} a,${s} li,${s} td,${s} th,${s} label,${s} div,${s} button,${s} input,${s} textarea{font-family:var(--font-body,inherit)!important}`);
  // Override Tailwind's font-sans class
  rules.push(`${s} .font-sans{font-family:var(--font-body,inherit)!important}`);
  rules.push(`${s} .font-heading{font-family:var(--font-heading,inherit)!important}`);

  return rules.join('\n');
})();

// ============================================================================
// ANIMATION KEYFRAMES CSS — defines all animation presets for the visual editor
// ============================================================================

const ANIMATION_KEYFRAMES_CSS = `
/* ── Entrance Animations ── */
@keyframes editorFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes editorFadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
@keyframes editorFadeInDown { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
@keyframes editorFadeInLeft { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
@keyframes editorFadeInRight { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
@keyframes editorZoomIn { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
@keyframes editorZoomInUp { from { opacity: 0; transform: scale(0.6) translateY(30px); } to { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes editorSlideInUp { from { opacity: 0; transform: translateY(60px); } to { opacity: 1; transform: translateY(0); } }
@keyframes editorSlideInDown { from { opacity: 0; transform: translateY(-60px); } to { opacity: 1; transform: translateY(0); } }
@keyframes editorSlideInLeft { from { opacity: 0; transform: translateX(-60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes editorSlideInRight { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes editorBounceIn {
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.05); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); }
}
@keyframes editorBounceInUp {
  0% { opacity: 0; transform: translateY(40px); }
  60% { opacity: 1; transform: translateY(-10px); }
  80% { transform: translateY(5px); }
  100% { transform: translateY(0); }
}
@keyframes editorFlipInX {
  from { opacity: 0; transform: perspective(400px) rotateX(90deg); }
  40% { transform: perspective(400px) rotateX(-10deg); }
  70% { transform: perspective(400px) rotateX(10deg); }
  to { opacity: 1; transform: perspective(400px) rotateX(0deg); }
}
@keyframes editorFlipInY {
  from { opacity: 0; transform: perspective(400px) rotateY(90deg); }
  40% { transform: perspective(400px) rotateY(-10deg); }
  70% { transform: perspective(400px) rotateY(10deg); }
  to { opacity: 1; transform: perspective(400px) rotateY(0deg); }
}
@keyframes editorRotateIn {
  from { opacity: 0; transform: rotate(-200deg); }
  to { opacity: 1; transform: rotate(0deg); }
}
@keyframes editorLightSpeedIn {
  from { opacity: 0; transform: translateX(100%) skewX(-30deg); }
  60% { opacity: 1; transform: translateX(-5%) skewX(10deg); }
  80% { transform: translateX(2%) skewX(-5deg); }
  to { transform: translateX(0) skewX(0deg); }
}
@keyframes editorJackInTheBox {
  0% { opacity: 0; transform: scale(0.1) rotate(30deg); transform-origin: center bottom; }
  50% { transform: rotate(-10deg); }
  70% { transform: rotate(3deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
}
@keyframes editorRollIn {
  from { opacity: 0; transform: translateX(-100%) rotate(-120deg); }
  to { opacity: 1; transform: translateX(0) rotate(0deg); }
}

/* ── Exit Animations ── */
@keyframes editorFadeOut { from { opacity: 1; } to { opacity: 0; } }
@keyframes editorFadeOutUp { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-30px); } }
@keyframes editorFadeOutDown { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(30px); } }
@keyframes editorZoomOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.6); } }
@keyframes editorSlideOutUp { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-60px); } }
@keyframes editorSlideOutDown { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(60px); } }
@keyframes editorBounceOut {
  20% { transform: scale(0.9); }
  50%, 55% { opacity: 1; transform: scale(1.1); }
  100% { opacity: 0; transform: scale(0.3); }
}

/* ── Hover effects are handled via inline styles, not keyframes ── */

/* ── Section animation wrapper ── */
.editor-section-animated {
  will-change: opacity, transform;
}
.editor-section-animated.anim-waiting {
  opacity: 0;
}
`;

// Map animation preset names to keyframe names
const ANIM_KEYFRAME_MAP = {
  fadeIn: 'editorFadeIn',
  fadeInUp: 'editorFadeInUp',
  fadeInDown: 'editorFadeInDown',
  fadeInLeft: 'editorFadeInLeft',
  fadeInRight: 'editorFadeInRight',
  zoomIn: 'editorZoomIn',
  zoomInUp: 'editorZoomInUp',
  slideInUp: 'editorSlideInUp',
  slideInDown: 'editorSlideInDown',
  slideInLeft: 'editorSlideInLeft',
  slideInRight: 'editorSlideInRight',
  bounceIn: 'editorBounceIn',
  bounceInUp: 'editorBounceInUp',
  flipInX: 'editorFlipInX',
  flipInY: 'editorFlipInY',
  rotateIn: 'editorRotateIn',
  lightSpeedIn: 'editorLightSpeedIn',
  jackInTheBox: 'editorJackInTheBox',
  rollIn: 'editorRollIn',
  // Exit
  fadeOut: 'editorFadeOut',
  fadeOutUp: 'editorFadeOutUp',
  fadeOutDown: 'editorFadeOutDown',
  zoomOut: 'editorZoomOut',
  slideOutUp: 'editorSlideOutUp',
  slideOutDown: 'editorSlideOutDown',
  bounceOut: 'editorBounceOut',
};

// Map hover preset names to CSS style objects
const HOVER_STYLE_MAP = {
  grow: { transform: 'scale(1.05)' },
  shrink: { transform: 'scale(0.95)' },
  lift: { transform: 'translateY(-8px)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)' },
  glow: { boxShadow: '0 0 20px rgba(99,102,241,0.4)' },
  brighten: { filter: 'brightness(1.1)' },
  darken: { filter: 'brightness(0.9)' },
  'blur-bg': { backdropFilter: 'blur(4px)' },
  'border-pop': { borderColor: 'var(--color-primary)', boxShadow: '0 0 0 3px var(--color-primary-light, rgba(99,102,241,0.3))' },
  'tilt-left': { transform: 'rotate(-3deg)' },
  'tilt-right': { transform: 'rotate(3deg)' },
};

// Lighten a hex color by mixing with white
const lightenColor = (hex, amount) => {
  if (!hex || typeof hex !== 'string') return hex;
  const num = parseInt(hex.replace('#', ''), 16);
  if (isNaN(num)) return hex;
  const r = Math.min(255, ((num >> 16) & 0xFF) + Math.round(255 * amount));
  const g = Math.min(255, ((num >> 8) & 0xFF) + Math.round(255 * amount));
  const b = Math.min(255, (num & 0xFF) + Math.round(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// Convert hex to "R,G,B" string for rgba() usage (matches customer-side themeUtils.ts)
const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return '255,255,255';
  const cleaned = hex.replace('#', '');
  if (cleaned.length === 3 || cleaned.length === 4) {
    return `${parseInt(cleaned[0]+cleaned[0],16)},${parseInt(cleaned[1]+cleaned[1],16)},${parseInt(cleaned[2]+cleaned[2],16)}`;
  }
  if (cleaned.length >= 6) {
    return `${parseInt(cleaned.substring(0,2),16)},${parseInt(cleaned.substring(2,4),16)},${parseInt(cleaned.substring(4,6),16)}`;
  }
  return '255,255,255';
};

// Mix two hex colors by weight (0-1). Matches customer-side themeUtils.ts mixHex().
const mixHex = (hex1, hex2, weight) => {
  const parse = (h) => {
    const c = h.replace('#', '');
    const full = c.length <= 4 ? (c[0]+c[0]+c[1]+c[1]+c[2]+c[2]) : c.substring(0, 6);
    return [parseInt(full.substring(0,2),16), parseInt(full.substring(2,4),16), parseInt(full.substring(4,6),16)];
  };
  const [r1,g1,b1] = parse(hex1);
  const [r2,g2,b2] = parse(hex2);
  const r = Math.round(r1*weight + r2*(1-weight));
  const g = Math.round(g1*weight + g2*(1-weight));
  const b = Math.round(b1*weight + b2*(1-weight));
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
};

// Icon mapping (same as StructureRenderer)
const ICON_MAP = {
  Search, SearchIcon: Search,
  Heart, HeartIcon: Heart,
  ShoppingCart, ShoppingCartIcon: ShoppingCart,
  User, UserIcon: User,
  Menu, MenuIcon: Menu,
  X, XIcon: X, CloseIcon: X,
  Home, HomeIcon: Home,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  ArrowRight, ArrowLeft, ArrowUp, ArrowDown,
  Star, StarIcon: Star,
  Check, CheckIcon: Check,
  CheckCircle, CheckCircleIcon: CheckCircle,
  AlertCircle, AlertCircleIcon: AlertCircle,
  Info, InfoIcon: Info,
  XCircle, XCircleIcon: XCircle,
  MapPin, MapPinIcon: MapPin,
  Phone, PhoneIcon: Phone,
  Mail, MailIcon: Mail,
  Facebook, FacebookIcon: Facebook,
  Instagram, InstagramIcon: Instagram,
  Twitter, TwitterIcon: Twitter,
  Youtube, YoutubeIcon: Youtube,
  Linkedin, LinkedinIcon: Linkedin,
  Play, PlayIcon: Play,
  Pause, PauseIcon: Pause,
  Package, PackageIcon: Package,
  Settings, SettingsIcon: Settings,
  LogOut, LogOutIcon: LogOut,
  Calendar, CalendarIcon: Calendar,
  Clock, ClockIcon: Clock,
  Plus, PlusIcon: Plus,
  Minus, MinusIcon: Minus,
  Edit, EditIcon: Edit,
  Trash2, Trash2Icon: Trash2,
  Eye, EyeIcon: Eye,
  EyeOff, EyeOffIcon: EyeOff,
  Filter, FilterIcon: Filter,
  Download, DownloadIcon: Download,
  Upload, UploadIcon: Upload,
  Share2, Share2Icon: Share2,
  Link2, Link2Icon: Link2,
  ImageIcon,
  Video, VideoIcon: Video,
  Music, MusicIcon: Music,
  File, FileIcon: File,
  Folder, FolderIcon: Folder,
  Send, SendIcon: Send,
  MessageCircle, MessageCircleIcon: MessageCircle,
  Bell, BellIcon: Bell,
  Globe, GlobeIcon: Globe,
  RefreshCw, RefreshCwIcon: RefreshCw,
  TrendingUp, TrendingUpIcon: TrendingUp,
  Award, AwardIcon: Award,
  Gift, GiftIcon: Gift,
  Tag, TagIcon: Tag,
  Bookmark, BookmarkIcon: Bookmark,
  Layers, LayersIcon: Layers,
  Grid, GridIcon: Grid,
  List, ListIcon: List,
  Layout, LayoutIcon: Layout,
  Type, TypeIcon: Type,
  Quote, QuoteIcon: Quote,
  Truck, TruckIcon: Truck, ShippingIcon: Truck,
  Shield, ShieldIcon: Shield,
  ShoppingBag, ShoppingBagIcon: ShoppingBag,
  CreditCard, CreditCardIcon: CreditCard,
  Lock, LockIcon: Lock,
  Wallet, WalletIcon: Wallet,
  BadgeCheck, BadgeCheckIcon: BadgeCheck,
  Store, StoreIcon: Store,
  Building, BuildingIcon: Building,
  HelpCircle, HelpCircleIcon: HelpCircle,
  FileText, FileTextIcon: FileText,
  BookOpen, BookOpenIcon: BookOpen,
  ShieldCheck, ShieldCheckIcon: ShieldCheck,
  AlertTriangle, AlertTriangleIcon: AlertTriangle,
  MessageSquare, MessageSquareIcon: MessageSquare,
  Headphones, HeadphonesIcon: Headphones,
  LifeBuoy, LifeBuoyIcon: LifeBuoy,
  Sparkles, SparklesIcon: Sparkles,
  Flame, FlameIcon: Flame,
  Zap, ZapIcon: Zap,
  Loader2, Loader2Icon: Loader2,
  Maximize, MaximizeIcon: Maximize,
  Minimize, MinimizeIcon: Minimize,
  Move, MoveIcon: Move,
  ExternalLink, ExternalLinkIcon: ExternalLink,
  Copy, CopyIcon: Copy,
  Smartphone, SmartphoneIcon: Smartphone,
  Tablet, TabletIcon: Tablet,
  Monitor, MonitorIcon: Monitor,
  Briefcase, BriefcaseIcon: Briefcase,
  PenTool, PenToolIcon: PenTool,
  Palette, PaletteIcon: Palette,
  Paintbrush, PaintbrushIcon: Paintbrush,
  Scissors, ScissorsIcon: Scissors,
  Ruler, RulerIcon: Ruler,
  Gem, GemIcon: Gem,
  Sun, SunIcon: Sun,
  Moon, MoonIcon: Moon,
  Camera, CameraIcon: Camera,
  Mic, MicIcon: Mic,
  Receipt, ReceiptIcon: Receipt,
  Banknote, BanknoteIcon: Banknote,
  CircleDollarSign, CircleDollarSignIcon: CircleDollarSign,
  DollarSign, DollarSignIcon: DollarSign,
  Percent, PercentIcon: Percent,
  BarChart, BarChartIcon: BarChart,
  Flag, FlagIcon: Flag,
  Anchor, AnchorIcon: Anchor,
  Compass, CompassIcon: Compass,
  Target, TargetIcon: Target,
  Crosshair, CrosshairIcon: Crosshair,
  AlignLeft, AlignCenter, AlignRight,
  Bold, Italic, Underline,
  AtSign, Hash,
  Key, KeyIcon: Key,
  Unlock, UnlockIcon: Unlock,
  Newspaper, NewspaperIcon: Newspaper,
  GraduationCap, GraduationCapIcon: GraduationCap,
  Library, LibraryIcon: Library,
  Shapes, Triangle, Square, Circle, Hexagon, Pentagon,
  FeatureIcon: Sparkles,
  ServiceIcon: Zap,
  DynamicIcon: RefreshCw,
};

// ============================================================================
// RESPONSIVE CLASS PROCESSOR (mobile/desktop preview)
// ============================================================================

/**
 * Process Tailwind classNames based on preview device.
 * In mobile mode: strip md:, lg:, xl:, 2xl: prefixed classes so that
 * "hidden lg:block" -> "hidden" (hidden on mobile)
 * "lg:hidden" -> "" (visible on mobile)
 * "text-2xl lg:text-3xl" -> "text-2xl" (mobile size)
 * "flex-col lg:flex-row" -> "flex-col" (mobile layout)
 *
 * Also scales down oversized text and spacing for mobile/tablet previews.
 */

// Scale large text sizes down for mobile preview
const MOBILE_TEXT_MAP = {
  'text-9xl': 'text-3xl', 'text-8xl': 'text-3xl', 'text-7xl': 'text-2xl',
  'text-6xl': 'text-2xl', 'text-5xl': 'text-xl', 'text-4xl': 'text-xl',
  'text-3xl': 'text-lg',
};
const TABLET_TEXT_MAP = {
  'text-9xl': 'text-4xl', 'text-8xl': 'text-4xl', 'text-7xl': 'text-3xl',
  'text-6xl': 'text-3xl', 'text-5xl': 'text-2xl', 'text-4xl': 'text-xl',
  'text-3xl': 'text-xl',
};

// Scale large spacing down for mobile preview
const MOBILE_SPACING_MAP = {
  // Vertical padding: keep generous for visual breathing room
  'py-24': 'py-8', 'py-20': 'py-6', 'py-16': 'py-6', 'py-14': 'py-5', 'py-12': 'py-5',
  'py-10': 'py-4', 'py-8': 'py-3',
  // Horizontal padding: keep enough so text doesn't hug edges
  'px-24': 'px-4', 'px-20': 'px-4', 'px-16': 'px-4', 'px-14': 'px-4', 'px-12': 'px-4',
  'px-10': 'px-4', 'px-8': 'px-3',
  'pt-24': 'pt-8', 'pt-20': 'pt-6', 'pt-16': 'pt-6', 'pt-12': 'pt-4', 'pt-10': 'pt-4', 'pt-8': 'pt-3',
  'pb-24': 'pb-8', 'pb-20': 'pb-6', 'pb-16': 'pb-6', 'pb-12': 'pb-4', 'pb-10': 'pb-4',
  'p-24': 'p-5', 'p-20': 'p-5', 'p-16': 'p-4', 'p-14': 'p-4', 'p-12': 'p-4',
  'p-10': 'p-4', 'p-8': 'p-3',
  // Gaps: keep reasonable so items don't touch
  'gap-16': 'gap-4', 'gap-12': 'gap-3', 'gap-10': 'gap-3', 'gap-8': 'gap-3', 'gap-6': 'gap-2',
  // Margins
  'my-24': 'my-8', 'my-20': 'my-6', 'my-16': 'my-5', 'my-12': 'my-4', 'my-10': 'my-4',
  'mx-24': 'mx-4', 'mx-20': 'mx-4', 'mx-16': 'mx-4', 'mx-12': 'mx-3', 'mx-10': 'mx-3',
  'mb-16': 'mb-5', 'mb-12': 'mb-4', 'mb-10': 'mb-3', 'mb-8': 'mb-3',
  'mt-16': 'mt-5', 'mt-12': 'mt-4', 'mt-10': 'mt-3',
  'space-y-16': 'space-y-4', 'space-y-12': 'space-y-3', 'space-y-10': 'space-y-3', 'space-y-8': 'space-y-3',
  'space-x-12': 'space-x-3', 'space-x-8': 'space-x-2', 'space-x-6': 'space-x-2',
  // Max-width utilities that overflow 375px
  'max-w-7xl': 'max-w-full', 'max-w-6xl': 'max-w-full', 'max-w-5xl': 'max-w-full',
  'max-w-4xl': 'max-w-full', 'max-w-3xl': 'max-w-full', 'max-w-2xl': 'max-w-full',
  'max-w-xl': 'max-w-full', 'max-w-lg': 'max-w-full',
  // Width utilities that could overflow
  'w-96': 'w-full', 'w-80': 'w-full', 'w-72': 'w-full', 'w-64': 'w-full',
  'w-60': 'w-full', 'w-48': 'w-full', 'w-40': 'w-32',
  // Column layout
  'columns-2': 'columns-1', 'columns-3': 'columns-1', 'columns-4': 'columns-1',
  // Negative margins causing horizontal scroll
  '-mx-4': 'mx-0', '-mx-6': 'mx-0', '-mx-8': 'mx-0',
  // Fractional widths: stack 3+ column flex layouts on mobile
  'w-1/3': 'w-full', 'w-1/4': 'w-1/2', 'w-1/6': 'w-1/2', 'w-1/5': 'w-1/2',
};
const TABLET_SPACING_MAP = {
  'py-24': 'py-10', 'py-20': 'py-8', 'py-16': 'py-6', 'py-12': 'py-6',
  'px-24': 'px-6', 'px-20': 'px-5', 'px-16': 'px-5', 'px-12': 'px-4',
  'pt-24': 'pt-10', 'pt-20': 'pt-8', 'pt-16': 'pt-6', 'pb-24': 'pb-10', 'pb-20': 'pb-8', 'pb-16': 'pb-6',
  'p-24': 'p-8', 'p-20': 'p-6', 'p-16': 'p-5', 'p-12': 'p-4',
  'gap-16': 'gap-4', 'gap-12': 'gap-3', 'gap-10': 'gap-3',
  'space-x-12': 'space-x-4', 'space-x-8': 'space-x-3',
  'max-w-7xl': 'max-w-full', 'max-w-6xl': 'max-w-full',
  'columns-3': 'columns-2', 'columns-4': 'columns-2',
};

// Map grid columns down for mobile
const MOBILE_GRID_MAP = {
  'grid-cols-4': 'grid-cols-2', 'grid-cols-3': 'grid-cols-1', 'grid-cols-2': 'grid-cols-2',
  'grid-cols-5': 'grid-cols-2', 'grid-cols-6': 'grid-cols-2',
};
const TABLET_GRID_MAP = {
  'grid-cols-4': 'grid-cols-2', 'grid-cols-3': 'grid-cols-2',
  'grid-cols-5': 'grid-cols-3', 'grid-cols-6': 'grid-cols-3',
};

/**
 * Categorize a Tailwind class by the CSS property it controls.
 * Used to determine when a responsive class overrides a base class.
 * e.g., "hidden" and "block" both control "display", so lg:block overrides hidden.
 */
const getClassProperty = (cls) => {
  // Display
  if (['hidden', 'block', 'inline-block', 'inline', 'flex', 'inline-flex', 'grid', 'inline-grid', 'contents', 'flow-root'].includes(cls)) return 'display';
  // Flex direction
  if (cls.startsWith('flex-row') || cls.startsWith('flex-col')) return 'flex-direction';
  // Flex wrap
  if (cls.startsWith('flex-wrap') || cls === 'flex-nowrap') return 'flex-wrap';
  // Justify
  if (cls.startsWith('justify-')) return 'justify-content';
  // Align items
  if (cls.startsWith('items-')) return 'align-items';
  // Text size
  if (cls.startsWith('text-') && cls.match(/^text-(xs|sm|base|lg|xl|[2-9]xl|\[)/)) return 'font-size';
  // Text align
  if (['text-left', 'text-center', 'text-right', 'text-justify'].includes(cls)) return 'text-align';
  // Text color (text-gray-900, text-white, etc.)
  if (cls.startsWith('text-')) return 'color';
  // Font weight
  if (cls.startsWith('font-')) return cls.startsWith('font-[') ? 'font-weight' : `font-${cls.split('-')[1]?.match(/^(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/) ? 'weight' : 'family'}`;
  // Background color
  if (cls.startsWith('bg-')) return 'background-color';
  // Border color
  if (cls.startsWith('border-') && !cls.match(/^border-(t|b|l|r|x|y)?-?\d/)) return 'border-color';
  // Border width
  if (cls.match(/^border(-[tblrxy])?(-\d|$)/)) return 'border-width';
  // Min/max width/height (check before w-/h-)
  if (cls.startsWith('min-w-')) return 'min-width';
  if (cls.startsWith('max-w-')) return 'max-width';
  if (cls.startsWith('min-h-')) return 'min-height';
  if (cls.startsWith('max-h-')) return 'max-height';
  // Width
  if (cls.startsWith('w-')) return 'width';
  // Height
  if (cls.startsWith('h-')) return 'height';
  // Padding (p-, px-, py-, pt-, pb-, pl-, pr-)
  if (cls.match(/^p[xytblr]-/)) return `padding-${cls[1]}`;
  if (cls.match(/^p-/)) return 'padding-all';
  // Margin (m-, mx-, my-, mt-, mb-, ml-, mr-, including negative)
  if (cls.match(/^-?m[xytblr]-/)) return `margin-${cls.replace(/^-/, '')[1]}`;
  if (cls.match(/^-?m-/)) return 'margin-all';
  // Gap
  if (cls.startsWith('gap-')) return 'gap';
  // Grid columns
  if (cls.startsWith('grid-cols-')) return 'grid-template-columns';
  if (cls.startsWith('col-span-') || cls === 'col-span-full') return 'grid-column';
  // Order
  if (cls.startsWith('order-')) return 'order';
  // Position
  if (['static', 'relative', 'absolute', 'fixed', 'sticky'].includes(cls)) return 'position';
  // Inset
  if (cls.startsWith('inset-') || cls.startsWith('top-') || cls.startsWith('right-') || cls.startsWith('bottom-') || cls.startsWith('left-')) return `inset-${cls.split('-')[0]}`;
  // Aspect ratio
  if (cls.startsWith('aspect-')) return 'aspect-ratio';
  // Space between
  if (cls.startsWith('space-x-') || cls.startsWith('space-y-')) return cls.startsWith('space-x') ? 'space-x' : 'space-y';
  // Columns
  if (cls.startsWith('columns-')) return 'columns';
  // Object fit
  if (cls.startsWith('object-')) return 'object-fit';
  // Overflow
  if (cls.startsWith('overflow-')) return `overflow-${cls.includes('-x-') ? 'x' : cls.includes('-y-') ? 'y' : 'all'}`;
  // Letter spacing
  if (cls.startsWith('tracking-')) return 'letter-spacing';
  // Line height
  if (cls.startsWith('leading-')) return 'line-height';
  // Border radius
  if (cls.startsWith('rounded')) return 'border-radius';
  // Opacity
  if (cls.startsWith('opacity-')) return 'opacity';
  // Z-index
  if (cls.startsWith('z-')) return 'z-index';
  // Whitespace
  if (cls.startsWith('whitespace-')) return 'white-space';
  // Default: use the class itself as its own "property" (no dedup)
  return `_${cls}`;
};

const processClassName = (className, previewDevice) => {
  if (!className) return className;

  // All max-* responsive prefixes (Tailwind v3.3+ max-width variants)
  const maxPrefixes = ['max-sm:', 'max-md:', 'max-lg:', 'max-xl:', 'max-2xl:'];
  const allPrefixes = ['sm:', 'md:', 'lg:', 'xl:', '2xl:', ...maxPrefixes];

  // Desktop: apply all responsive classes as unprefixed (highest breakpoint wins).
  // The editor canvas is narrower than real desktop viewport, so lg:/xl:/2xl:
  // classes wouldn't naturally activate. We force them on.
  // max-* classes are stripped on desktop (simulates a wide viewport).
  if (previewDevice === 'desktop') {
    const allClasses = className.split(/\s+/);
    const minPrefixes = ['sm:', 'md:', 'lg:', 'xl:', '2xl:'];

    // Start with base classes (no prefix, no max-* prefix)
    const baseClasses = allClasses.filter(cls => !allPrefixes.some(p => cls.startsWith(p)));

    // Apply responsive classes in order (later breakpoints override earlier)
    // This simulates a wide desktop viewport where all breakpoints are active
    const responsiveOverrides = new Map(); // property -> class value
    for (const prefix of minPrefixes) {
      allClasses.filter(cls => cls.startsWith(prefix)).forEach(cls => {
        const unprefixed = cls.slice(prefix.length);
        const prop = getClassProperty(unprefixed);
        responsiveOverrides.set(prop, unprefixed);
      });
    }

    // Build final class list: base classes filtered to remove those overridden, plus overrides
    const overrideValues = new Set(responsiveOverrides.values());
    const overrideProps = new Set(responsiveOverrides.keys());
    const finalClasses = baseClasses.filter(cls => {
      const prop = getClassProperty(cls);
      // Keep base class only if no responsive override for the same property
      return !overrideProps.has(prop);
    });
    finalClasses.push(...overrideValues);

    return finalClasses.join(' ') || undefined;
  }

  const isMobile = previewDevice === 'mobile';
  const textMap = isMobile ? MOBILE_TEXT_MAP : TABLET_TEXT_MAP;
  const spacingMap = isMobile ? MOBILE_SPACING_MAP : TABLET_SPACING_MAP;
  const gridMap = isMobile ? MOBILE_GRID_MAP : TABLET_GRID_MAP;

  const classes = className.split(/\s+/).filter(cls => {
    // Strip all max-* prefixed classes from the base filter — they are handled separately below
    if (maxPrefixes.some(p => cls.startsWith(p))) return false;
    if (isMobile) {
      // Mobile (375px): strip sm:, md:, lg:, xl:, 2xl: — only base classes survive
      return !cls.startsWith('sm:') && !cls.startsWith('md:') && !cls.startsWith('lg:') && !cls.startsWith('xl:') && !cls.startsWith('2xl:');
    }
    // tablet (768px): apply sm: and md:, strip lg: and above
    return !cls.startsWith('lg:') && !cls.startsWith('xl:') && !cls.startsWith('2xl:');
  });

  if (isMobile) {
    // Mobile (375px < 640px): apply max-sm:, max-md:, max-lg:, max-xl:, max-2xl: (all apply)
    const mobileMaxPrefixes = ['max-sm:', 'max-md:', 'max-lg:', 'max-xl:', 'max-2xl:'];
    for (const prefix of mobileMaxPrefixes) {
      className.split(/\s+/)
        .filter(cls => cls.startsWith(prefix))
        .forEach(cls => classes.push(cls.slice(prefix.length)));
    }
  } else {
    // For tablet, apply sm: and md: prefixed classes (remove prefix)
    const smClasses = className.split(/\s+/)
      .filter(cls => cls.startsWith('sm:'))
      .map(cls => cls.replace('sm:', ''));
    const mdClasses = className.split(/\s+/)
      .filter(cls => cls.startsWith('md:'))
      .map(cls => cls.replace('md:', ''));
    classes.push(...smClasses, ...mdClasses);
    // Tablet (768px < 1024px): apply max-lg:, max-xl:, max-2xl:
    const tabletMaxPrefixes = ['max-lg:', 'max-xl:', 'max-2xl:'];
    for (const prefix of tabletMaxPrefixes) {
      className.split(/\s+/)
        .filter(cls => cls.startsWith(prefix))
        .forEach(cls => classes.push(cls.slice(prefix.length)));
    }
  }

  // Apply size/spacing/grid mappings
  const mapped = classes.map(cls => {
    if (textMap[cls]) return textMap[cls];
    if (spacingMap[cls]) return spacingMap[cls];
    if (gridMap[cls]) return gridMap[cls];

    // Mobile: only convert flex-row to flex-col when it's a layout container
    // (has children that each take significant width). Skip for small inline rows.
    if (isMobile && cls === 'flex-row') {
      // Check if this is a layout container by looking at sibling classes
      const hasLargeGap = /\bgap-(8|10|12|16|20|24)\b/.test(className);
      const hasWideChildren = /\bw-1\/[23]\b/.test(className) || /\bw-\[(?:4[0-9]{2,}|[5-9]\d{2,})px\]/.test(className);
      const isLayout = hasLargeGap || hasWideChildren || /\bjustify-between\b/.test(className);
      if (isLayout) return 'flex-col';
      // Small inline rows (icon+text, price+button) stay horizontal
    }

    // Mobile: convert fixed widths that overflow 375px
    if (isMobile) {
      const fixedW = cls.match(/^w-\[(\d+)px\]$/);
      if (fixedW && parseInt(fixedW[1], 10) > 340) return 'w-full';
      const minW = cls.match(/^min-w-\[(\d+)px\]$/);
      if (minW && parseInt(minW[1], 10) > 340) return 'min-w-full';
      // Convert vw units to px for mobile preview (375px viewport simulation)
      const vwMatch = cls.match(/^w-\[(\d+)vw\]$/);
      if (vwMatch) {
        const vw = parseInt(vwMatch[1], 10);
        const px = Math.round(375 * vw / 100);
        return `w-[${px}px]`;
      }
      // Scale arbitrary text sizes (text-[2rem], text-[24px] etc.)
      const remMatch = cls.match(/^text-\[(\d+(?:\.\d+)?)rem\]$/);
      if (remMatch) {
        const rem = parseFloat(remMatch[1]);
        if (rem >= 2) return 'text-lg';
        if (rem >= 1.5) return 'text-base';
      }
      const pxTextMatch = cls.match(/^text-\[(\d+)px\]$/);
      if (pxTextMatch) {
        const px = parseInt(pxTextMatch[1], 10);
        if (px >= 24) return 'text-base';
        // Small pixel sizes are fine on mobile
      }
      // Column spans: force full-width on mobile
      if (cls.match(/^col-span-[2-9]$/) || cls.match(/^col-span-1[0-2]$/)) return 'col-span-full';
      if (cls === 'row-span-2') return '';
      // grid-cols-12 → single column on mobile
      if (cls === 'grid-cols-12') return 'grid-cols-1';
    }

    // Tablet: convert widths
    if (!isMobile) {
      const fixedW = cls.match(/^w-\[(\d+)px\]$/);
      if (fixedW && parseInt(fixedW[1], 10) > 720) return 'w-full';
      // Convert vw units for 768px preview
      const vwMatch = cls.match(/^w-\[(\d+)vw\]$/);
      if (vwMatch) {
        const px = Math.round(768 * parseInt(vwMatch[1], 10) / 100);
        return `w-[${px}px]`;
      }
      if (cls === 'grid-cols-12') return 'grid-cols-6';
    }

    return cls;
  });

  return mapped.join(' ') || undefined;
};

/**
 * Strip classes that break editor layout from special component placeholders.
 * Removes: absolute positioning, full-screen heights, inset, fixed, sticky
 */
const sanitizePlaceholderClassName = (className) => {
  if (!className) return '';
  const STRIP = new Set([
    'absolute', 'fixed', 'sticky',
    'inset-0', 'inset-x-0', 'inset-y-0',
    'top-0', 'left-0', 'right-0', 'bottom-0',
    'h-screen', 'min-h-screen', 'h-full', 'min-h-full',
    'w-screen', 'w-full',
    'overflow-hidden', 'overflow-auto', 'overflow-scroll',
    'z-10', 'z-20', 'z-30', 'z-40', 'z-50',
  ]);
  return className
    .split(/\s+/)
    .filter(cls => !STRIP.has(cls))
    .join(' ');
};

/**
 * Compute inline style overrides for editor mode.
 * Caps viewport-height classes to manageable sizes using inline styles
 * (Tailwind JIT won't generate CSS for runtime arbitrary values).
 * Device-aware: smaller caps for mobile/tablet previews.
 */
const getEditorStyleOverrides = (className, editMode, previewDevice = 'desktop') => {
  if (!editMode || !className) return null;
  const overrides = {};

  // Desktop: let content flow naturally (canvas is scrollable).
  // Mobile/Tablet: gentle caps to keep device frame usable.
  if (previewDevice === 'desktop') {
    // No height caps for desktop — the canvas scrolls naturally
  } else {
    const isMobile = previewDevice === 'mobile';
    if (/\bh-screen\b/.test(className) || /\bh-\[100vh\]/.test(className)) {
      overrides.height = isMobile ? '500px' : '500px';
    }
    if (/\bmin-h-screen\b/.test(className) || /\bmin-h-\[100vh\]/.test(className)) {
      overrides.minHeight = isMobile ? '450px' : '450px';
    }
  }

  // Handle aspect-ratio arbitrary values that Tailwind JIT can't generate at runtime
  const aspectMatch = className.match(/\baspect-\[(\d+)\/(\d+)\]/);
  if (aspectMatch) {
    overrides.aspectRatio = `${aspectMatch[1]}/${aspectMatch[2]}`;
  }

  // Handle arbitrary fixed heights (h-[500px], h-[400px], etc.)
  const heightMatch = className.match(/\bh-\[(\d+)px\]/);
  if (heightMatch) {
    const px = parseInt(heightMatch[1], 10);
    if (previewDevice === 'mobile') {
      overrides.height = `${Math.min(px, 400)}px`;
    } else if (previewDevice === 'tablet') {
      overrides.height = `${Math.min(px, 500)}px`;
    }
    // Desktop: use original height
  }

  // Handle arbitrary vh heights (h-[85vh], h-[90vh], etc.) — Tailwind JIT won't generate these.
  // For desktop, use the LAST match to pick up responsive overrides (e.g. md:h-[90vh] after h-[85vh]).
  // For mobile/tablet, use the FIRST match (base value before responsive overrides).
  const vhMatches = [...className.matchAll(/\bh-\[(\d+)vh\]/g)];
  if (vhMatches.length > 0) {
    const vh = parseInt(previewDevice === 'desktop'
      ? vhMatches[vhMatches.length - 1][1]
      : vhMatches[0][1], 10);
    if (previewDevice === 'desktop') {
      overrides.height = `${vh}vh`;
    } else if (previewDevice === 'mobile') {
      overrides.height = `${Math.min(Math.round(vh * 5), 500)}px`;
    } else {
      overrides.height = `${Math.min(Math.round(vh * 7), 700)}px`;
    }
  }

  // Handle min-h-[Xvh] too
  const minVhMatch = className.match(/\bmin-h-\[(\d+)vh\]/);
  if (minVhMatch) {
    const vh = parseInt(minVhMatch[1], 10);
    if (previewDevice === 'desktop') {
      overrides.minHeight = `${vh}vh`;
    } else if (previewDevice === 'mobile') {
      overrides.minHeight = `${Math.min(Math.round(vh * 5), 450)}px`;
    } else {
      overrides.minHeight = `${Math.min(Math.round(vh * 7), 600)}px`;
    }
  }

  // Handle arbitrary w-[Xvw] widths (Tailwind JIT may not generate these at runtime)
  const vwWidthMatch = className.match(/\bw-\[(\d+)vw\]/);
  if (vwWidthMatch) {
    const vw = parseInt(vwWidthMatch[1], 10);
    if (previewDevice === 'mobile') {
      overrides.width = `${Math.round(375 * vw / 100)}px`;
    } else if (previewDevice === 'tablet') {
      overrides.width = `${Math.round(768 * vw / 100)}px`;
    } else {
      overrides.width = `${vw}vw`;
    }
  }

  // Handle arbitrary min-w-[Xpx] values
  const minWMatch = className.match(/\bmin-w-\[(\d+)px\]/);
  if (minWMatch) {
    overrides.minWidth = `${minWMatch[1]}px`;
  }

  return Object.keys(overrides).length > 0 ? overrides : null;
};

// ============================================================================
// INLINE TEXT EDITOR (embedded - lightweight contentEditable)
// ============================================================================

const PLACEHOLDER_LABELS = { h1: 'Heading', h2: 'Heading', h3: 'Subheading', h4: 'Subheading', p: 'Paragraph text', span: 'Text', button: 'Button text', a: 'Link text', li: 'List item' };

const InlineTextEditor = ({ value, onSave, className, style, tag: Tag = 'span', onElementSelect, bindingPath }) => {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const originalValue = useRef(value);
  const editingRef = useRef(false);
  const isEmpty = !value || value.trim() === '';

  // Sync DOM content with value prop ONLY when not editing
  // This prevents React from clobbering user input mid-edit
  useEffect(() => {
    if (!editingRef.current && ref.current) {
      const placeholderLabel = isEmpty
        ? PLACEHOLDER_LABELS[Tag] || 'Text'
        : null;
      ref.current.innerHTML = placeholderLabel || value || '';
    }
  }, [value, isEmpty, Tag]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    // Don't preventDefault — allow browser to place cursor at click position
    if (!editing) {
      setEditing(true);
      editingRef.current = true;
      originalValue.current = value;
      if (isEmpty && ref.current) {
        ref.current.innerText = '';
      }
    }
    onElementSelect?.({ type: 'text', domNode: ref.current, bindingPath });
  }, [editing, value, isEmpty, onElementSelect, bindingPath]);

  useEffect(() => {
    if (editing && ref.current) {
      // Only move cursor to end if there's no existing selection (i.e., user clicked on empty/placeholder)
      const sel = window.getSelection();
      const hasSelectionInElement = sel && sel.rangeCount > 0 && ref.current.contains(sel.anchorNode);
      if (!hasSelectionInElement) {
        ref.current.focus();
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [editing]);

  const handleBlur = useCallback(() => {
    if (ref.current) {
      const newText = ref.current.innerText;
      if (newText !== originalValue.current) {
        onSave(newText);
      }
    }
    setEditing(false);
    editingRef.current = false;
  }, [onSave]);

  const handleKeyDown = useCallback((e) => {
    // Stop all key events from bubbling to parent handlers (dnd-kit, shortcuts)
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ref.current?.blur();
    }
    if (e.key === 'Escape') {
      if (ref.current) {
        ref.current.innerText = originalValue.current;
      }
      setEditing(false);
      editingRef.current = false;
    }
  }, []);

  const placeholderLabel = isEmpty && !editing
    ? PLACEHOLDER_LABELS[Tag] || 'Text'
    : null;

  return React.createElement(Tag, {
    ref,
    className: (className || '') + (editing ? '' : ' cursor-text'),
    style: {
      ...style,
      outline: editing ? '2px solid #6366f1' : 'none',
      outlineOffset: editing ? '2px' : '0px',
      minWidth: '20px',
      minHeight: isEmpty ? '1.2em' : undefined,
      borderRadius: editing ? '3px' : '2px',
      transition: 'outline 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
      caretColor: editing ? '#6366f1' : undefined,
      boxShadow: editing ? '0 0 0 4px rgba(99,102,241,0.08)' : (hovered ? '0 0 0 2px rgba(99,102,241,0.06)' : 'none'),
      background: editing ? 'rgba(99,102,241,0.02)' : (hovered ? 'rgba(99,102,241,0.03)' : undefined),
      ...(placeholderLabel ? { fontStyle: 'italic', opacity: 0.45 } : {})
    },
    contentEditable: true,
    suppressContentEditableWarning: true,
    onClick: handleClick,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  });
};

// ============================================================================
// IMAGE OVERLAY EDITOR
// ============================================================================

const ImageOverlay = ({ src, alt, className, style, onReplace, onElementSelect, bindingPath }) => {
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      onReplace(file);
      e.target.value = '';
    }
  }, [onReplace]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onElementSelect?.({ type: 'image', domNode: imgRef.current, bindingPath });
    fileInputRef.current?.click();
  }, [onElementSelect, bindingPath]);

  const srcStr = (typeof src === 'string') ? src : '';
  const hasValidSrc = srcStr && !srcStr.includes('{{') && srcStr.length > 2;

  // Camera button that floats in the corner — always accessible even when
  // text elements overlay the image (z-index 20 sits above text overlays).
  // Positioned absolute within the parent's relative container.
  // onMouseEnter keeps button visible when mouse moves from img to button.
  const cameraButton = (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'absolute', top: '10px', right: '10px', zIndex: 20,
        opacity: isHovered ? 1 : 0,
        transform: isHovered ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(-4px)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: isHovered ? 'auto' : 'none'
      }}
    >
      <div
        style={{
          background: 'rgba(10, 15, 28, 0.9)', color: '#fff', borderRadius: '10px',
          padding: '7px 14px', cursor: 'pointer', display: 'flex',
          alignItems: 'center', gap: '7px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
          border: 'none',
          transition: 'all 0.15s ease',
        }}
        onClick={handleClick}
        onMouseEnter={e => { e.currentTarget.style.background = '#6366f1'; e.currentTarget.style.transform = 'scale(1.03)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(10, 15, 28, 0.9)'; e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <Camera style={{ width: '14px', height: '14px', color: '#fff' }} />
        <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#fff', letterSpacing: '0.01em' }}>Replace</span>
      </div>
    </div>
  );

  // Render img directly (no wrapper div) so layout is identical to preview.
  // Do NOT add z-index — it creates a stacking context that covers sibling
  // text containers (e.g., hero text over background image).
  // Instead, gradient overlays get pointer-events:none in edit mode.
  // The camera button provides a z-indexed affordance for image replacement
  // even when text overlays cover the image.
  if (hasValidSrc) {
    return (
      <>
        <img
          ref={imgRef}
          src={srcStr}
          alt={alt || ''}
          className={className || ''}
          style={{
            ...style,
            cursor: 'pointer',
            outline: isHovered ? '2px solid #6366f1' : 'none',
            outlineOffset: '2px',
            transition: 'outline 0.15s ease, box-shadow 0.15s ease',
            boxShadow: isHovered ? '0 0 0 4px rgba(99,102,241,0.1)' : 'none',
            borderRadius: style?.borderRadius || undefined,
          }}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
        {cameraButton}
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
      </>
    );
  }

  // No valid src — show placeholder
  return (
    <>
      <div
        className={(className || '').replace(/\bobject-\w+\b/g, '').trim()}
        style={{
          ...style,
          minHeight: '60px',
          background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          outline: isHovered ? '3px solid #3b82f6' : 'none',
          outlineOffset: '-3px',
          transition: 'outline 0.15s ease'
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={{ textAlign: 'center', color: '#9ca3af' }}>
          <Camera style={{ width: '32px', height: '32px', margin: '0 auto 4px', opacity: 0.5, color: '#9ca3af' }} />
          <span style={{ fontSize: '10px', opacity: 0.7, color: '#9ca3af' }}>{alt || 'Upload Image'}</span>
        </div>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
    </>
  );
};

// ============================================================================
// RESOLVE HELPERS
// ============================================================================

const resolveDataBindingEditor = (content, data, loopContext) => {
  return resolveBindings(content, data, loopContext);
};

const resolveLoopData = (dataPath, data) => {
  const resolved = resolveDataPath(dataPath, data);
  if (Array.isArray(resolved)) return resolved;
  if (resolved && typeof resolved === 'object' && Array.isArray(resolved.items)) {
    return resolved.items;
  }
  return [];
};

/**
 * Evaluate a template condition - matches customer-side StructureRenderer behavior.
 * Supports: boolean strings, string comparisons, numeric comparisons,
 * array length checks, logical operators, negation, existence checks.
 */
const evaluateCondition = (condition, data, loopContext) => {
  if (!condition) return true;
  const context = { ...data, ...loopContext };

  // Boolean strings
  if (condition === 'true') return true;
  if (condition === 'false') return false;

  // Negation: "!variable"
  if (typeof condition === 'string' && condition.startsWith('!')) {
    return !evaluateCondition(condition.slice(1).trim(), data, loopContext);
  }

  if (typeof condition === 'string') {
    // String comparisons: 'status' === 'active'
    const stringCompMatch = condition.match(/^['"](.+?)['"]\s*(===|!==|==|!=)\s*['"](.+?)['"]$/);
    if (stringCompMatch) {
      const left = stringCompMatch[1];
      const right = stringCompMatch[3];
      const op = stringCompMatch[2];
      if (op === '===' || op === '==') return left === right;
      return left !== right;
    }

    // General comparisons: variable === value, variable > 5, etc.
    const compMatch = condition.match(/^(.+?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/);
    if (compMatch) {
      const resolveVal = (v) => {
        v = v.trim();
        const strLit = v.match(/^['"](.*)['"]$/);
        if (strLit) return strLit[1];
        if (!isNaN(Number(v)) && v.trim() !== '') return Number(v);
        if (v === 'true') return true;
        if (v === 'false') return false;
        if (v === 'null') return null;
        if (v === 'undefined') return undefined;
        return resolveDataPath(v, context);
      };

      const left = resolveVal(compMatch[1]);
      const right = resolveVal(compMatch[3]);
      const op = compMatch[2];

      switch (op) {
        case '===': case '==': return left === right;
        case '!==': case '!=': return left !== right;
        case '>': return left > right;
        case '<': return left < right;
        case '>=': return left >= right;
        case '<=': return left <= right;
        default: return false;
      }
    }

    // Array length checks: "products.length > 0"
    const lengthCompMatch = condition.match(/^(.+?)\.length\s*(>|>=|<|<=|===|==)\s*(\d+)$/);
    if (lengthCompMatch) {
      const arr = resolveDataPath(lengthCompMatch[1].trim(), context);
      const len = Array.isArray(arr) ? arr.length : (typeof arr === 'string' ? arr.length : 0);
      const num = parseInt(lengthCompMatch[3], 10);
      const op = lengthCompMatch[2];
      switch (op) {
        case '>': return len > num;
        case '>=': return len >= num;
        case '<': return len < num;
        case '<=': return len <= num;
        case '===': case '==': return len === num;
        default: return false;
      }
    }

    // Logical operators: && and ||
    if (condition.includes('&&')) {
      return condition.split('&&').every(part => evaluateCondition(part.trim(), data, loopContext));
    }
    if (condition.includes('||')) {
      return condition.split('||').some(part => evaluateCondition(part.trim(), data, loopContext));
    }

    // .includes() check
    const includesMatch = condition.match(/^(.+?)\.includes\(['"](.+?)['"]\)$/);
    if (includesMatch) {
      const val = resolveDataPath(includesMatch[1].trim(), context);
      if (Array.isArray(val)) return val.includes(includesMatch[2]);
      if (typeof val === 'string') return val.includes(includesMatch[2]);
      return false;
    }
  }

  // Existence/truthy checks: resolve the value and check
  const resolvedCondition = typeof condition === 'string'
    ? resolveDataBindingEditor(condition, data, loopContext) || condition
    : condition;

  const value = resolveDataPath(resolvedCondition, context);
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(value);
};

// URL safety check — matches customer-side StructureRenderer
const isSafeUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith('javascript:') || trimmed.startsWith('data:text/html') || trimmed.startsWith('vbscript:')) {
    return false;
  }
  return true;
};

// HTML content detection — matches customer-side StructureRenderer
const containsHTML = (str) => {
  if (!str || typeof str !== 'string') return false;
  return /<[a-z][\s\S]*>/i.test(str);
};

// Filter valid HTML attributes (prevent React warnings)
const COMMON_ATTRS = new Set([
  'id', 'className', 'title', 'style', 'role', 'tabIndex',
  'draggable', 'hidden', 'lang', 'dir'
]);

const TAG_ATTRS = {
  a: new Set(['href', 'target', 'rel', 'download']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading', 'decoding']),
  video: new Set(['src', 'poster', 'controls', 'autoPlay', 'loop', 'muted', 'playsInline']),
  input: new Set(['type', 'name', 'value', 'placeholder', 'disabled', 'required', 'checked']),
  textarea: new Set(['name', 'value', 'placeholder', 'disabled', 'required', 'rows', 'cols']),
  select: new Set(['name', 'value', 'disabled', 'required', 'multiple']),
  button: new Set(['type', 'disabled']),
  form: new Set(['action', 'method']),
  label: new Set(['htmlFor']),
  option: new Set(['value', 'selected', 'disabled']),
};

const filterHTMLAttrs = (tag, attrs) => {
  const tagAttrs = TAG_ATTRS[tag];
  const filtered = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (COMMON_ATTRS.has(key) || key.startsWith('data-') || key.startsWith('aria-') ||
        (tagAttrs && tagAttrs.has(key))) {
      filtered[key] = value;
    }
  }
  return filtered;
};

const parseCSSString = (cssStr) => {
  if (!cssStr || typeof cssStr !== 'string') return {};
  const obj = {};
  cssStr.split(';').forEach(rule => {
    const colonIdx = rule.indexOf(':');
    if (colonIdx === -1) return;
    const prop = rule.slice(0, colonIdx).trim();
    const val = rule.slice(colonIdx + 1).trim();
    if (prop && val) {
      const camelProp = prop.replace(/-([a-z])/g, (_, l) => l.toUpperCase());
      obj[camelProp] = val;
    }
  });
  return obj;
};

// ============================================================================
// ELEMENT RENDERER (Core recursive component)
// ============================================================================

const EditableElement = React.memo(({
  structure,
  data,
  loopContext,
  onContentChange,
  onImageReplace,
  editMode,
  nodePath,
  imageBaseUrl,
  previewDevice = 'desktop',
  onElementSelect,
  selectedNodePath,
  editingNodePath,
  onElementClick,
  onElementDoubleClick,
  onOpenConfigPanel
}) => {
  // ALL hooks must be called before any early returns (React rules of hooks)

  const resolvedType = useMemo(() => {
    if (!structure || typeof structure.type !== 'string') return structure?.type || '';
    return resolveDataBindingEditor(structure.type, data, loopContext);
  }, [structure?.type, data, loopContext]);

  const resolvedContent = useMemo(() => {
    if (!structure?.content) return '';
    return resolveDataBindingEditor(structure.content, data, loopContext);
  }, [structure?.content, data, loopContext]);

  const resolvedAttributes = useMemo(() => {
    if (!structure?.attributes) return {};
    const resolved = {};
    for (const [key, value] of Object.entries(structure.attributes)) {
      if (typeof value === 'string') {
        const singleBinding = value.match(/^\{\{([^}]+)\}\}$/);
        if (singleBinding) {
          const path = singleBinding[1].trim();
          let objVal = loopContext
            ? resolveDataPath(path, loopContext) || resolveDataPath(path, data)
            : resolveDataPath(path, data);
          // Ensure src/poster are always strings (binding may resolve to array/object)
          if (key === 'src' || key === 'poster') {
            if (Array.isArray(objVal)) objVal = objVal[0] || '';
            if (typeof objVal !== 'string') objVal = '';
            if (objVal && imageBaseUrl && !objVal.startsWith('http') && !objVal.startsWith('blob:')) {
              objVal = `${imageBaseUrl}${objVal}`;
            }
          }
          resolved[key] = objVal;
        } else if (key === 'style' && !value.startsWith('{{')) {
          resolved[key] = parseCSSString(value);
        } else if (key === 'src' && !value.startsWith('{{')) {
          const resolvedUrl = resolveDataBindingEditor(value, data, loopContext);
          const hasRealUrl = resolvedUrl && resolvedUrl.length > 0;
          resolved[key] = (hasRealUrl && imageBaseUrl && !resolvedUrl.startsWith('http') && !resolvedUrl.startsWith('blob:'))
            ? `${imageBaseUrl}${resolvedUrl}` : resolvedUrl;
        } else {
          resolved[key] = resolveDataBindingEditor(value, data, loopContext);
        }
      } else {
        resolved[key] = value;
      }
    }
    return resolved;
  }, [structure?.attributes, data, loopContext, imageBaseUrl]);

  // Process className for responsive preview
  const pClassName = useMemo(() => {
    return processClassName(structure?.className, previewDevice);
  }, [structure?.className, previewDevice]);

  // Compute inline style overrides for editor height capping (device-aware)
  const editorOverrides = useMemo(() => {
    return getEditorStyleOverrides(structure?.className, editMode, previewDevice);
  }, [structure?.className, editMode, previewDevice]);

  // Early return after all hooks
  if (!structure || typeof structure !== 'object' || !structure.type) return null;

  // Merge base style with editor overrides (height capping etc.)
  // Also strip internal _prefixed keys (used by PropertiesPanel for transform/filter decomposition)
  const rawMergedStyle = editorOverrides
    ? { ...structure.style, ...editorOverrides }
    : structure.style;
  let mergedStyle = rawMergedStyle;
  if (rawMergedStyle && typeof rawMergedStyle === 'object') {
    const keys = Object.keys(rawMergedStyle);
    if (keys.some(k => k.startsWith('_'))) {
      mergedStyle = {};
      for (const k of keys) {
        if (!k.startsWith('_')) mergedStyle[k] = rawMergedStyle[k];
      }
    }
  }

  // Shared props for recursive children
  const childProps = { data, onContentChange, onImageReplace, editMode, imageBaseUrl, previewDevice, onElementSelect, selectedNodePath, editingNodePath, onElementClick, onElementDoubleClick, onOpenConfigPanel };

  // Handle conditional rendering
  if (structure.condition) {
    const condResult = evaluateCondition(structure.condition.condition, data, loopContext);
    if (!condResult) {
      if (structure.condition.elseStructure) {
        return (
          <EditableElement
            structure={structure.condition.elseStructure}
            loopContext={loopContext}
            nodePath={nodePath}
            {...childProps}
          />
        );
      }
      return null;
    }
  }

  // Handle loop rendering
  if (structure.loop) {
    const loopData = resolveLoopData(structure.loop.data, { ...data });
    if (loopData.length === 0) {
      // For product loops with no data, show preview components instead of empty div
      const loopDataPath = structure.loop.data || '';
      if (loopDataPath === 'products' || loopDataPath === 'content.products') {
        return <ShopProductGridPreview attributes={resolvedAttributes || {}} />;
      }
      if (loopDataPath === 'cartItems' || loopDataPath === 'content.cartItems') {
        return <CartPreview attributes={resolvedAttributes || {}} />;
      }
      const Tag = (structure.type || 'div');
      return React.createElement(Tag, { className: pClassName || '', style: mergedStyle });
    }

    const hasLayoutClasses = pClassName &&
      (pClassName.includes('grid') || pClassName.includes('flex'));

    if (hasLayoutClasses) {
      const Tag = resolvedType || 'div';
      const children = [];
      loopData.forEach((item, index) => {
        const newLoopContext = {
          ...loopContext,
          [structure.loop.itemVar]: item,
          ...(structure.loop.indexVar ? { [structure.loop.indexVar]: index } : {}),
          __loopDataPath: structure.loop.data,
          __loopItemVar: structure.loop.itemVar,
          __loopIndex: index,
        };
        structure.children?.forEach((child, ci) => {
          children.push(
            <EditableElement
              key={`${index}-${ci}`}
              structure={child}
              loopContext={newLoopContext}
              nodePath={`${nodePath}.children.${ci}`}
              {...childProps}
            />
          );
        });
      });
      return React.createElement(Tag, { className: pClassName, style: mergedStyle }, children);
    } else {
      const elements = [];
      loopData.forEach((item, index) => {
        const newLoopContext = {
          ...loopContext,
          [structure.loop.itemVar]: item,
          ...(structure.loop.indexVar ? { [structure.loop.indexVar]: index } : {}),
          __loopDataPath: structure.loop.data,
          __loopItemVar: structure.loop.itemVar,
          __loopIndex: index,
        };
        const Tag = resolvedType || 'div';
        const itemKey = item?.id || item?.slug || `loop-${index}`;
        const childElements = [];
        structure.children?.forEach((child, ci) => {
          childElements.push(
            <EditableElement
              key={`${itemKey}-${ci}`}
              structure={child}
              loopContext={newLoopContext}
              nodePath={`${nodePath}.children.${ci}`}
              {...childProps}
            />
          );
        });
        elements.push(React.createElement(Tag, {
          key: itemKey,
          className: pClassName,
          style: mergedStyle
        }, childElements));
      });
      return <>{elements}</>;
    }
  }

  // Handle icon type
  if (resolvedType === 'icon') {
    const rawName = resolvedAttributes.name || structure.name || '';
    const iconName = resolveDataBindingEditor(rawName, data, loopContext);
    const IconComp = ICON_MAP[iconName] || ICON_MAP[iconName + 'Icon'];
    if (IconComp) return <IconComp className={pClassName || ''} style={structure.style} />;
    return null;
  }

  // Handle *Icon suffix types
  if (typeof resolvedType === 'string' && resolvedType.endsWith('Icon')) {
    const baseName = resolvedType.replace(/Icon$/, '');
    const IconComp = ICON_MAP[resolvedType] || ICON_MAP[baseName];
    if (IconComp) {
      const size = resolvedAttributes.size ? parseInt(resolvedAttributes.size, 10) : undefined;
      return <IconComp className={pClassName || ''} style={structure.style} size={size} />;
    }
    return null;
  }

  // Handle icon components by name in ICON_MAP
  if (ICON_MAP[resolvedType]) {
    const IconComp = ICON_MAP[resolvedType];
    const size = resolvedAttributes.size ? parseInt(resolvedAttributes.size, 10) : undefined;
    return <IconComp className={pClassName || ''} style={structure.style} size={size} />;
  }

  // ======== RENDER-THROUGH TYPES ========
  // These types have proper children in the JSON — render as <div> with children
  const RENDER_THROUGH_TYPES = new Set([
    'NewsletterForm', 'HeroSlider', 'HeroMedia', 'Carousel',
    'SocialLinksGroup',
    'TestimonialCarousel'
  ]);

  if (RENDER_THROUGH_TYPES.has(resolvedType)) {
    const Tag = 'div';
    const kids = [];
    if (resolvedContent) kids.push(resolvedContent);

    // Some render-through types have inline loop data (data/itemVar props)
    // e.g., TestimonialCarousel with data="content.testimonials.items" itemVar="testimonial"
    let childLoopContext = loopContext;
    if (structure.data && structure.itemVar && structure.children?.length > 0) {
      const loopItems = resolveLoopData(structure.data, { ...data });
      if (loopItems.length > 0) {
        // Render first item as preview (carousel shows one at a time)
        childLoopContext = {
          ...loopContext,
          [structure.itemVar]: loopItems[0],
        };
      }
    }

    if (structure.children?.length > 0) {
      structure.children.forEach((child, index) => {
        kids.push(
          <EditableElement
            key={index}
            structure={child}
            loopContext={childLoopContext}
            nodePath={`${nodePath}.children.${index}`}
            {...childProps}
          />
        );
      });
    }
    return React.createElement(Tag, { className: pClassName || undefined, style: mergedStyle }, kids.length > 0 ? kids : undefined);
  }

  // ======== PRODUCT GRID — render placeholder product cards ========
  if (resolvedType === 'ProductGrid') {
    const isGrid = pClassName && pClassName.includes('grid');
    const isFlex = pClassName && pClassName.includes('flex');
    const isMobilePreview = previewDevice === 'mobile';
    const isTabletPreview = previewDevice === 'tablet';
    const placeholderCount = isMobilePreview ? 2 : (isTabletPreview ? (isGrid ? 4 : 4) : (isGrid ? 4 : 6));
    const placeholders = Array.from({ length: placeholderCount }, (_, i) => (
      <div key={i} className={(!isMobilePreview && isFlex) ? 'flex-none snap-start' : ''} style={(!isMobilePreview && isFlex) ? { width: '44%' } : undefined}>
        <div style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)', borderRadius: '8px', marginBottom: '8px', aspectRatio: '3/4' }}>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShoppingBag style={{ width: '32px', height: '32px', color: '#d1d5db' }} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '4px', width: '75%' }} />
          <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '4px', width: '50%' }} />
        </div>
      </div>
    ));
    // For mobile: use grid-cols-2 instead of flex snap for proper fit
    if (isMobilePreview) {
      return (
        <div className="grid grid-cols-2 gap-3 p-3" style={mergedStyle}>
          {placeholders}
        </div>
      );
    }
    return <div className={pClassName || ''} style={mergedStyle}>{placeholders}</div>;
  }

  // ======== COUNTDOWN — render static countdown numbers ========
  if (resolvedType === 'Countdown') {
    const units = [
      { value: '02', label: 'Days' }, { value: '14', label: 'Hrs' },
      { value: '36', label: 'Min' }, { value: '52', label: 'Sec' }
    ];
    return (
      <div className={pClassName || 'flex items-center gap-2'} style={mergedStyle}>
        {units.map((u, i) => (
          <React.Fragment key={i}>
            <div className="text-center">
              <span className="text-lg font-bold">{u.value}</span>
              <span className="text-[10px] block opacity-70">{u.label}</span>
            </div>
            {i < 3 && <span className="text-lg font-bold opacity-50">:</span>}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // ======== MARQUEE TEXT — render scrolling text ========
  if (resolvedType === 'MarqueeText') {
    const text = resolvedContent || resolvedAttributes?.text || 'New Collection Available';
    const repeat = parseInt(resolvedAttributes?.repeat || '4', 10);
    return (
      <div className={pClassName || 'overflow-hidden whitespace-nowrap'} style={mergedStyle}>
        <div className="inline-flex animate-marquee">
          {Array.from({ length: repeat }, (_, i) => (
            <span key={i} className={resolvedAttributes?.itemClassName || 'mx-8 text-sm font-medium'}>{text}</span>
          ))}
        </div>
      </div>
    );
  }

  // Navbar preview - render actual preview instead of placeholder
  if (resolvedType === 'NavbarWrapper') {
    return (
      <div style={{ position: 'relative' }}>
        <NavbarPreview
          attributes={resolvedAttributes || {}}
          editMode={editMode}
          themeColors={data?.theme?.colors}
        />
        {editMode && onOpenConfigPanel && (
          <div
            onClick={() => onOpenConfigPanel('navbar')}
            style={{
              position: 'absolute', inset: 0, cursor: 'pointer', zIndex: 200,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', transition: 'background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.querySelector('[data-config-label]').style.opacity = '1'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('[data-config-label]').style.opacity = '0'; }}
          >
            <span data-config-label="true" style={{
              opacity: 0, transition: 'opacity 0.2s',
              background: '#4f46e5', color: '#fff', fontSize: '11px', fontWeight: 600,
              padding: '6px 14px', borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(79,70,229,0.3)',
              pointerEvents: 'none',
            }}>Click to Edit Navigation Bar</span>
          </div>
        )}
      </div>
    );
  }

  // Footer preview - render actual preview instead of placeholder
  if (resolvedType === 'FooterWrapper') {
    return (
      <div style={{ position: 'relative' }}>
        <FooterPreview
          attributes={resolvedAttributes || {}}
          editMode={editMode}
          themeColors={data?.theme?.colors}
        />
        {editMode && onOpenConfigPanel && (
          <div
            onClick={() => onOpenConfigPanel('footer')}
            style={{
              position: 'absolute', inset: 0, cursor: 'pointer', zIndex: 200,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', transition: 'background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.querySelector('[data-config-label]').style.opacity = '1'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('[data-config-label]').style.opacity = '0'; }}
          >
            <span data-config-label="true" style={{
              opacity: 0, transition: 'opacity 0.2s',
              background: '#4f46e5', color: '#fff', fontSize: '11px', fontWeight: 600,
              padding: '6px 14px', borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(79,70,229,0.3)',
              pointerEvents: 'none',
            }}>Click to Edit Footer</span>
          </div>
        )}
      </div>
    );
  }

  // Shop product grid preview
  if (resolvedType === 'EnhancedProductCard') {
    return <ShopProductGridPreview attributes={resolvedAttributes || {}} />;
  }

  // Product gallery preview - only the gallery, since the template has the info as HTML
  if (resolvedType === 'ProductGallery') {
    const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 500'%3E%3Crect fill='%23f1f5f9' width='400' height='500'/%3E%3Ctext fill='%2394a3b8' font-family='system-ui' font-size='14' text-anchor='middle' dominant-baseline='middle' x='200' y='250'%3EProduct Image%3C/text%3E%3C/svg%3E";
    return (
      <div>
        <div style={{ aspectRatio: '4/5', background: '#f8fafc', borderRadius: '12px', overflow: 'hidden', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={PLACEHOLDER_IMG} alt="Product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{ width: '64px', height: '80px', borderRadius: '8px', border: i === 0 ? '2px solid #111827' : '2px solid #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Product info preview - standalone product detail
  if (resolvedType === 'ProductInfo') {
    return <ProductDetailPreview attributes={resolvedAttributes || {}} />;
  }

  // Login/Signup previews
  if (resolvedType === 'LoginFormWrapper' || resolvedType === 'LoginForm') {
    return <LoginFormPreview attributes={resolvedAttributes || {}} formType="login" />;
  }
  if (resolvedType === 'SignupFormWrapper' || resolvedType === 'SignupForm') {
    return <LoginFormPreview attributes={resolvedAttributes || {}} formType="signup" />;
  }

  // Cart previews
  if (resolvedType === 'CartListWrapper' || resolvedType === 'CartContentWrapper' || resolvedType === 'CartItemsList') {
    return <CartPreview attributes={resolvedAttributes || {}} />;
  }

  // Checkout previews
  if (resolvedType === 'CheckoutForm') {
    return <CheckoutPreview attributes={resolvedAttributes || {}} />;
  }

  // Contact form preview
  if (resolvedType === 'ContactFormWrapper' || resolvedType === 'ContactForm') {
    return <ContactFormPreview attributes={resolvedAttributes || {}} />;
  }

  // Contact page special components
  if (resolvedType === 'ContactInfoCard') {
    return (
      <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px' }}>
        {[{ icon: Mail, label: 'Email', value: 'info@yourbrand.com' }, { icon: Phone, label: 'Phone', value: '+91 98765 43210' }, { icon: MapPin, label: 'Location', value: 'Design District, Mumbai' }].map(({ icon: Icon, label, value }, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: i < 2 ? '1px solid #e5e7eb' : 'none' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon style={{ width: '16px', height: '16px', color: '#4f46e5' }} />
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500 }}>{label}</div>
              <div style={{ fontSize: '13px', color: '#111827', fontWeight: 500 }}>{value}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (resolvedType === 'BusinessHoursCard') {
    return (
      <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Business Hours</div>
        {[{ day: 'Mon - Fri', time: '9:00 AM - 6:00 PM' }, { day: 'Saturday', time: '10:00 AM - 4:00 PM' }, { day: 'Sunday', time: 'Closed' }].map((h, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '12px' }}>
            <span style={{ color: '#6b7280' }}>{h.day}</span>
            <span style={{ color: '#111827', fontWeight: 500 }}>{h.time}</span>
          </div>
        ))}
      </div>
    );
  }

  if (resolvedType === 'SocialMediaCard') {
    return (
      <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Follow Us</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[Instagram, Facebook, Twitter, Youtube].map((Icon, i) => (
            <div key={i} style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}>
              <Icon style={{ width: '16px', height: '16px', color: '#4f46e5' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (resolvedType === 'FAQGrid') {
    return (
      <div style={{ display: 'grid', gap: '12px', padding: '12px 0' }}>
        {['What are your shipping options?', 'What is your return policy?', 'How can I track my order?', 'Do you offer custom sizing?'].map((q, i) => (
          <div key={i} style={{ padding: '16px', background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{q}</span>
              <ChevronDown style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Cart page header preview
  if (resolvedType === 'CartPageHeader') {
    return (
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ArrowLeft style={{ width: '18px', height: '18px', color: '#6b7280' }} />
          <span style={{ fontSize: '13px', color: '#6b7280' }}>Continue Shopping</span>
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: 0 }}>Shopping Cart</h2>
        <span style={{ fontSize: '13px', color: '#6b7280', background: '#f3f4f6', padding: '4px 10px', borderRadius: '12px' }}>3 items</span>
      </div>
    );
  }

  // Checkout header / progress steps preview
  if (resolvedType === 'Header' || resolvedType === 'CheckoutProgressSteps') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', padding: '20px 24px' }}>
        {[{ label: 'Cart', done: true }, { label: 'Shipping', active: true }, { label: 'Payment' }, { label: 'Complete' }].map((step, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div style={{ width: '40px', height: '2px', background: step.done || step.active ? '#4f46e5' : '#e5e7eb' }} />}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 600,
                background: step.done ? '#4f46e5' : step.active ? '#111827' : '#f3f4f6',
                color: step.done || step.active ? '#fff' : '#9ca3af',
                border: step.active ? '2px solid #111827' : 'none',
              }}>
                {step.done ? <Check style={{ width: '14px', height: '14px' }} /> : i + 1}
              </div>
              <span style={{ fontSize: '11px', fontWeight: step.active ? 600 : 400, color: step.active ? '#111827' : '#9ca3af' }}>{step.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  }

  // Order items list preview
  if (resolvedType === 'OrderItemsList') {
    return (
      <div style={{ padding: '8px 0' }}>
        {[{ name: 'Classic Cotton Kurta', qty: 1, price: '₹2,999' }, { name: 'Silk Saree', qty: 1, price: '₹8,999' }].map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i === 0 ? '1px solid #f3f4f6' : 'none' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{item.name}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>Qty: {item.qty}</div>
            </div>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{item.price}</span>
          </div>
        ))}
      </div>
    );
  }

  // Product Actions preview (Add to Cart, Wishlist, Size/Quantity selectors)
  if (resolvedType === 'ProductActions') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['S', 'M', 'L', 'XL', 'XXL'].map(s => (
            <button key={s} style={{
              width: '40px', height: '40px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
              border: s === 'M' ? '2px solid #111827' : '1px solid #e5e7eb',
              background: s === 'M' ? '#111827' : '#fff', color: s === 'M' ? '#fff' : '#374151',
              cursor: 'default',
            }}>{s}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>Quantity</span>
          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <span style={{ padding: '6px 12px', fontSize: '14px', color: '#9ca3af' }}>−</span>
            <span style={{ padding: '6px 16px', fontSize: '14px', fontWeight: 600, borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>1</span>
            <span style={{ padding: '6px 12px', fontSize: '14px', color: '#374151' }}>+</span>
          </div>
        </div>
        <button style={{
          width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
          background: '#111827', color: '#fff', fontSize: '14px', fontWeight: 600,
          cursor: 'default', letterSpacing: '0.02em',
        }}>Add to Cart</button>
        <button style={{
          width: '100%', padding: '12px', borderRadius: '10px',
          border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
          fontSize: '13px', fontWeight: 500, cursor: 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <Heart style={{ width: '16px', height: '16px' }} /> Add to Wishlist
        </button>
      </div>
    );
  }

  // ======== VISUAL COMPONENT PREVIEWS ========
  // These render realistic previews matching customer-side StructureRenderer output
  // instead of generic placeholders.

  if (resolvedType === 'Rating') {
    const rating = resolvedAttributes.value || 4;
    const max = resolvedAttributes.max || 5;
    return (
      <div className={pClassName || 'flex items-center gap-1'} style={mergedStyle}>
        {Array.from({ length: max }, (_, i) => (
          <Star key={i} style={{ width: '16px', height: '16px', color: i < rating ? '#f59e0b' : '#e5e7eb', fill: i < rating ? '#f59e0b' : 'none' }} />
        ))}
        {resolvedAttributes.showCount && <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>(24)</span>}
      </div>
    );
  }

  if (resolvedType === 'Breadcrumbs') {
    const items = resolvedAttributes.items || [
      { label: 'Home', url: '/' }, { label: 'Shop', url: '/shop' }, { label: 'Product' }
    ];
    return (
      <nav className={pClassName || 'flex items-center gap-2 text-sm'} style={mergedStyle}>
        {items.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight style={{ width: '14px', height: '14px', color: '#9ca3af' }} />}
            <span style={{ color: i === items.length - 1 ? '#111827' : '#6b7280', fontWeight: i === items.length - 1 ? 500 : 400, fontSize: '13px' }}>{item.label}</span>
          </React.Fragment>
        ))}
      </nav>
    );
  }

  if (resolvedType === 'Filter') {
    return (
      <div className={pClassName || ''} style={{ ...mergedStyle, padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Filters</span>
          <Filter style={{ width: '16px', height: '16px', color: '#6b7280' }} />
        </div>
        {['Category', 'Size', 'Color', 'Price Range'].map((label, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '13px', color: '#374151' }}>{label}</span>
            <ChevronDown style={{ width: '14px', height: '14px', color: '#9ca3af' }} />
          </div>
        ))}
      </div>
    );
  }

  if (resolvedType === 'EmptyCart') {
    return (
      <div className={pClassName || ''} style={{ ...mergedStyle, textAlign: 'center', padding: '48px 24px' }}>
        <ShoppingCart style={{ width: '48px', height: '48px', color: '#d1d5db', margin: '0 auto 16px' }} />
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>Your cart is empty</h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>Looks like you haven't added anything to your cart yet</p>
        <button style={{ padding: '10px 24px', background: '#111827', color: '#fff', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 500, cursor: 'default' }}>Continue Shopping</button>
      </div>
    );
  }

  if (resolvedType === 'PriceBreakdown') {
    return (
      <div className={pClassName || ''} style={{ ...mergedStyle, padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
        {[{ label: 'Subtotal', value: '\u20B911,998' }, { label: 'Shipping', value: 'Free' }, { label: 'Tax', value: '\u20B9600' }].map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', color: '#6b7280' }}>
            <span>{item.label}</span><span style={{ fontWeight: 500, color: '#111827' }}>{item.value}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', marginTop: '8px', borderTop: '2px solid #e5e7eb', fontSize: '15px', fontWeight: 700, color: '#111827' }}>
          <span>Total</span><span>{'\u20B9'}12,598</span>
        </div>
      </div>
    );
  }

  if (resolvedType === 'CouponSection') {
    return (
      <div className={pClassName || ''} style={{ ...mergedStyle, padding: '12px', border: '1px dashed #d1d5db', borderRadius: '8px', display: 'flex', gap: '8px' }}>
        <input type="text" placeholder="Enter coupon code" disabled style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', background: '#fff' }} />
        <button style={{ padding: '8px 16px', background: '#111827', color: '#fff', borderRadius: '6px', border: 'none', fontSize: '13px', fontWeight: 500, cursor: 'default' }}>Apply</button>
      </div>
    );
  }

  if (resolvedType === 'CheckoutButton') {
    return (
      <button className={pClassName || ''} style={{ ...mergedStyle, width: '100%', padding: '14px', background: '#111827', color: '#fff', borderRadius: '10px', border: 'none', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>
        Proceed to Checkout
      </button>
    );
  }

  if (resolvedType === 'Pagination') {
    return (
      <div className={pClassName || 'flex items-center justify-center gap-2'} style={mergedStyle}>
        <button style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft style={{ width: '14px', height: '14px', color: '#9ca3af' }} /></button>
        {[1, 2, 3].map(n => (
          <button key={n} style={{ width: '32px', height: '32px', borderRadius: '6px', border: n === 1 ? '1px solid #111827' : '1px solid #e5e7eb', background: n === 1 ? '#111827' : '#fff', color: n === 1 ? '#fff' : '#374151', fontSize: '13px', fontWeight: 500, cursor: 'default' }}>{n}</button>
        ))}
        <button style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight style={{ width: '14px', height: '14px', color: '#9ca3af' }} /></button>
      </div>
    );
  }

  if (resolvedType === 'SearchWithAutocomplete') {
    return (
      <div className={pClassName || ''} style={{ ...mergedStyle, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff' }}>
          <Search style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
          <span style={{ fontSize: '13px', color: '#9ca3af' }}>Search products...</span>
        </div>
      </div>
    );
  }

  if (resolvedType === 'ContactFormNew') {
    return <ContactFormPreview attributes={resolvedAttributes || {}} />;
  }

  // Special components - render as placeholder boxes in editor (truly interactive only)
  const SPECIAL_TYPES = new Set([
    'VideoPlayer', 'ImageZoom', 'Modal', 'Tabs', 'Accordion', 'MasonryGrid',
    'QuantitySelector', 'ColorSwatches', 'SizeSelector',
    'PriceRange', 'ShareButtons', 'ProgressBar',
    'Badge', 'Loader', 'Skeleton', 'EmptyState',
    'GridListToggle', 'FilterDrawer', 'ProductQuickView',
    'SimilarProducts', 'CartSummaryWrapper',
    'StickyWrapper', 'CompareProducts', 'ProductPriceDisplay'
  ]);

  if (SPECIAL_TYPES.has(resolvedType)) {
    // Friendly labels for component types (fashion designers don't know React component names)
    const FRIENDLY_LABELS = {
      VideoPlayer: 'Video Player', ImageZoom: 'Zoomable Image', Modal: 'Popup Window',
      Tabs: 'Tabbed Content', Accordion: 'Expandable Sections', MasonryGrid: 'Photo Grid',
      QuantitySelector: 'Quantity Picker', ColorSwatches: 'Color Options', SizeSelector: 'Size Picker',
      PriceRange: 'Price Filter', ShareButtons: 'Share Buttons', ProgressBar: 'Progress Bar',
      Badge: 'Badge', Loader: 'Loading Spinner', Skeleton: 'Loading Placeholder', EmptyState: 'Empty State',
      GridListToggle: 'View Switcher', FilterDrawer: 'Filter Panel', ProductQuickView: 'Quick Preview',
      SimilarProducts: 'Similar Products', CartSummaryWrapper: 'Cart Summary',
      StickyWrapper: 'Sticky Section', CompareProducts: 'Product Comparison', ProductPriceDisplay: 'Price Display',
    };
    const rawClass = pClassName || '';
    const cleanClass = sanitizePlaceholderClassName(rawClass);
    // If original class had absolute/inset positioning, this component was meant to fill its parent
    const wasAbsoluteFill = /\babsolute\b/.test(rawClass) && /\binset-0\b/.test(rawClass);
    const fillClass = wasAbsoluteFill ? 'h-full w-full' : 'min-h-[100px]';
    return (
      <div
        className={`${cleanClass} ${fillClass}`}
        style={{ ...mergedStyle, border: '2px dashed #d1d5db', borderRadius: '8px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(249,250,251,0.8)' }}
      >
        <div style={{ textAlign: 'center', color: '#9ca3af' }}>
          <Layers style={{ width: '20px', height: '20px', margin: '0 auto 4px', opacity: 0.6, color: '#9ca3af' }} />
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af' }}>{FRIENDLY_LABELS[resolvedType] || resolvedType}</span>
          <p style={{ fontSize: '10px', marginTop: '2px', opacity: 0.75, color: '#9ca3af' }}>This will be interactive on your live site</p>
        </div>
      </div>
    );
  }

  // ======== EDITABLE TEXT ========
  // Always render InlineTextEditor for text elements in edit mode.
  // Single-click activates editing AND triggers element selection (toolbar/properties).
  if (editMode && isEditableText(structure)) {
    let bindingPath = extractBindingPath(structure.content);

    // Resolve loop variable bindings to actual content paths
    if (bindingPath && loopContext?.__loopItemVar && bindingPath.startsWith(loopContext.__loopItemVar + '.')) {
      const fieldInItem = bindingPath.slice(loopContext.__loopItemVar.length + 1);
      const dataPath = loopContext.__loopDataPath || '';
      const index = loopContext.__loopIndex;
      if (dataPath && index !== undefined) {
        bindingPath = `${dataPath}.${index}.${fieldInItem}`;
      }
    }

    const hasIconChildren = structure.children?.length > 0;
    const Tag = resolvedType || 'span';

    // Combined select callback: triggers both legacy onElementSelect and new onElementClick
    const handleTextSelect = (info) => {
      onElementSelect?.(info);
      onElementClick?.({ type: 'text', domNode: info?.domNode, nodePath, structure, tagName: Tag, bindingPath: bindingPath || nodePath });
    };

    if (hasIconChildren) {
      return React.createElement(Tag, { className: pClassName || '', style: mergedStyle }, [
        <InlineTextEditor
          key="text"
          value={resolvedContent}
          tag="span"
          className=""
          style={{}}
          onSave={(newText) => bindingPath ? onContentChange(bindingPath, newText) : onContentChange(null, newText, nodePath)}
          onElementSelect={handleTextSelect}
          bindingPath={bindingPath || nodePath}
        />,
        ...structure.children.map((child, index) => (
          <EditableElement
            key={`icon-${index}`}
            structure={child}
            loopContext={loopContext}
            nodePath={`${nodePath}.children.${index}`}
            {...childProps}
          />
        ))
      ]);
    }

    return (
      <InlineTextEditor
        value={resolvedContent}
        tag={resolvedType}
        className={pClassName || ''}
        style={mergedStyle}
        onSave={(newText) => bindingPath ? onContentChange(bindingPath, newText) : onContentChange(null, newText, nodePath)}
        onElementSelect={handleTextSelect}
        bindingPath={bindingPath || nodePath}
      />
    );
  }

  // ======== EDITABLE IMAGE ========
  if (editMode && isEditableImage(structure)) {
    const imgSrc = typeof resolvedAttributes.src === 'string' ? resolvedAttributes.src : '';
    let bindingPath = structure.attributes?.src ? extractBindingPath(structure.attributes.src) : null;

    if (bindingPath && loopContext?.__loopItemVar && bindingPath.startsWith(loopContext.__loopItemVar + '.')) {
      const fieldInItem = bindingPath.slice(loopContext.__loopItemVar.length + 1);
      const dataPath = loopContext.__loopDataPath || '';
      const index = loopContext.__loopIndex;
      if (dataPath && index !== undefined) {
        bindingPath = `${dataPath}.${index}.${fieldInItem}`;
      }
    }

    const isImgSelected = selectedNodePath === nodePath;

    return (
      <ImageOverlay
        src={imgSrc}
        alt={resolvedAttributes.alt || ''}
        className={pClassName || ''}
        style={{
          ...mergedStyle,
          outline: isImgSelected ? '2px solid #3b82f6' : mergedStyle?.outline,
          outlineOffset: isImgSelected ? '2px' : undefined,
          boxShadow: isImgSelected ? '0 0 0 4px rgba(59,130,246,0.12)' : mergedStyle?.boxShadow,
        }}
        onReplace={(file) => onImageReplace(bindingPath, file, nodePath)}
        onElementSelect={(info) => {
          onElementSelect?.(info);
          onElementClick?.({ type: 'image', domNode: info?.domNode, nodePath, structure, tagName: 'img', bindingPath: bindingPath || nodePath });
        }}
        bindingPath={bindingPath || nodePath}
      />
    );
  }

  // ======== EDITABLE BACKGROUND IMAGE ========
  if (editMode && isEditableBackgroundImage(structure)) {
    const bgUrl = extractBackgroundImageUrl(structure, data, loopContext);
    const resolvedBgUrl = bgUrl && imageBaseUrl && !bgUrl.startsWith('http') && !bgUrl.startsWith('blob:') && !bgUrl.startsWith('{{')
      ? `${imageBaseUrl}${bgUrl}` : bgUrl;
    const hasValidBg = resolvedBgUrl && !resolvedBgUrl.includes('{{') && resolvedBgUrl.length > 2;

    // Render children normally inside a relative container, with camera overlay
    const Tag = resolvedType && new Set(['div','section','header','footer','main','article','aside','figure','nav']).has(resolvedType)
      ? resolvedType : 'div';

    const bgStyle = {
      ...mergedStyle,
      backgroundImage: hasValidBg ? `url(${resolvedBgUrl})` : undefined,
      backgroundSize: mergedStyle?.backgroundSize || 'cover',
      backgroundPosition: mergedStyle?.backgroundPosition || 'center',
    };

    const fileInputRef = React.createRef();

    return (
      <Tag className={`relative ${pClassName || ''}`} style={bgStyle}>
        {structure.children?.map((child, index) => (
          <EditableElement
            key={index}
            structure={child}
            loopContext={loopContext}
            nodePath={`${nodePath}.children.${index}`}
            {...childProps}
          />
        ))}
        {resolvedContent && !structure.children?.length && (
          /<[^>]+>/.test(resolvedContent)
            ? <span dangerouslySetInnerHTML={{ __html: resolvedContent }} />
            : resolvedContent
        )}
        {/* Background change button — uses inline styles to avoid theme CSS overrides */}
        <div
          className="absolute top-2 right-2 z-30 opacity-0 hover:opacity-100 transition-opacity duration-200"
          style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 30, pointerEvents: 'auto' }}
        >
          <div
            style={{ background: 'rgba(0,0,0,0.75)', color: '#fff', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          >
            <Camera style={{ width: '16px', height: '16px', color: '#fff' }} />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>{hasValidBg ? 'Change Background' : 'Add Background Image'}</span>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImageReplace(null, file, nodePath);
          }}
        />
      </Tag>
    );
  }

  // ======== STANDARD HTML ELEMENTS ========
  const htmlTags = new Set([
    'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'a', 'img', 'ul', 'ol', 'li', 'nav', 'header', 'footer',
    'main', 'section', 'article', 'aside', 'figure', 'figcaption',
    'form', 'input', 'textarea', 'button', 'select', 'option', 'label',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'video', 'audio', 'source', 'iframe', 'canvas',
    'br', 'hr', 'strong', 'em', 'b', 'i', 'u', 'small', 'sub', 'sup',
    'blockquote', 'pre', 'code', 'details', 'summary', 'time', 'mark', 'cite',
    'dl', 'dt', 'dd', 'fieldset', 'legend', 'progress', 'meter', 'picture', 'abbr', 'address'
  ]);

  const Tag = htmlTags.has(resolvedType) ? resolvedType : 'div';
  const isSelected = selectedNodePath === nodePath;

  // URL safety: sanitize href/src attributes
  if (resolvedAttributes.href && !isSafeUrl(resolvedAttributes.href)) {
    resolvedAttributes.href = '#';
  }
  if (resolvedAttributes.src && !isSafeUrl(resolvedAttributes.src)) {
    resolvedAttributes.src = '';
  }

  // Build props
  const props = {
    className: pClassName || undefined,
    style: mergedStyle,
    ...filterHTMLAttrs(Tag, resolvedAttributes)
  };

  // In edit mode, add selection click handler to all elements
  if (editMode && onElementClick) {
    const existingOnClick = props.onClick;
    props.onClick = (e) => {
      e.stopPropagation();
      if (existingOnClick) existingOnClick(e);
      onElementClick({ type: 'element', domNode: e.currentTarget, nodePath, structure, tagName: Tag });
    };

    // Selection visual feedback
    if (isSelected) {
      props.style = {
        ...props.style,
        outline: '2px solid #3b82f6',
        outlineOffset: '2px',
        boxShadow: '0 0 0 4px rgba(59,130,246,0.1)',
        transition: 'outline 0.15s ease, box-shadow 0.15s ease',
      };
    }
  }

  // In edit mode, prevent navigation on links and form submission on buttons
  if (editMode) {
    if (Tag === 'a') {
      const wrappedClick = props.onClick;
      props.onClick = (e) => { e.preventDefault(); if (wrappedClick) wrappedClick(e); };
      props.style = { ...props.style, cursor: 'pointer' };
    }
    if (Tag === 'button') {
      const wrappedClick = props.onClick;
      props.onClick = (e) => { e.preventDefault(); if (wrappedClick) wrappedClick(e); };
    }
    if (Tag === 'form') {
      props.onSubmit = (e) => e.preventDefault();
    }
  }

  // In edit mode, make gradient/color overlay divs non-interactive so they don't
  // block clicks on images or text underneath. Detect overlays as: div with
  // absolute+inset-0 positioning, a bg-* class, and no meaningful text content.
  if (editMode && Tag === 'div') {
    const cn = structure.className || '';
    const isAbsoluteOverlay = /\babsolute\b/.test(cn) && /\binset-0\b/.test(cn);
    const hasBgClass = /\bbg-/.test(cn);
    const hasNoContent = !resolvedContent;
    const hasNoChildren = !structure.children || structure.children.length === 0;
    if (isAbsoluteOverlay && hasBgClass && hasNoContent && hasNoChildren) {
      props.style = { ...props.style, pointerEvents: 'none' };
    }
  }

  // Self-closing/void elements — matches customer-side StructureRenderer
  const voidElements = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'param', 'source', 'track', 'wbr'];
  if (voidElements.includes(Tag) && Tag !== 'img') {
    // img handled below with placeholder logic
    return React.createElement(Tag, props);
  }

  // Images: show placeholder when src is empty/missing
  if (Tag === 'img') {
    const rawImgSrc = props.src || resolvedAttributes.src || '';
    const imgSrc = typeof rawImgSrc === 'string' ? rawImgSrc : '';
    if (!imgSrc || imgSrc === '{{' || imgSrc.includes('{{')) {
      // Replace broken img with a styled placeholder that preserves original positioning
      // Keep the original className so absolute/relative/sizing classes are retained
      const origClass = pClassName || '';
      return (
        <div
          className={origClass}
          style={{ ...mergedStyle, minHeight: '80px', background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ textAlign: 'center', color: '#9ca3af' }}>
            <ImageIcon style={{ width: '40px', height: '40px', margin: '0 auto 4px', opacity: 0.4, color: '#9ca3af' }} />
            <span style={{ fontSize: '12px', opacity: 0.6, color: '#9ca3af' }}>{resolvedAttributes.alt || 'Image'}</span>
          </div>
        </div>
      );
    }
    // Add lazy loading to images — matches customer-side StructureRenderer
    if (!props.loading) props.loading = 'lazy';
    return React.createElement(Tag, props);
  }

  // Video element defaults — matches customer-side StructureRenderer
  if (Tag === 'video') {
    if (props.autoPlay === undefined) props.autoPlay = true;
    if (props.muted === undefined) props.muted = true;
    if (props.loop === undefined) props.loop = true;
    if (props.playsInline === undefined) props.playsInline = true;
  }

  // ======== PRICE AUTO-ENHANCEMENT ========
  // Matches customer-side StructureRenderer: auto-detects price patterns in
  // product loop context and enhances with strikethrough + discount badge.
  if (!editMode && loopContext && resolvedContent && !structure.children?.length) {
    const loopItem = loopContext[loopContext.__loopItemVar];
    if (loopItem && loopItem.Discountedprice && loopItem.price) {
      const hasDiscount = parseFloat(loopItem.Discountedprice) < parseFloat(loopItem.price);
      if (hasDiscount) {
        const displayPrice = `\u20B9${parseFloat(loopItem.Discountedprice).toLocaleString('en-IN')}`;
        const originalPrice = `\u20B9${parseFloat(loopItem.price).toLocaleString('en-IN')}`;
        // Check if this element displays the discounted price
        const contentStr = String(resolvedContent).replace(/[,\s]/g, '');
        const priceStr = displayPrice.replace(/[,\s]/g, '');
        if (contentStr === priceStr || contentStr.includes(priceStr)) {
          const discountPercent = Math.round((1 - parseFloat(loopItem.Discountedprice) / parseFloat(loopItem.price)) * 100);
          return React.createElement(Tag, props, [
            <span key="price">{displayPrice}</span>,
            <span key="original" style={{ textDecoration: 'line-through', opacity: 0.5, marginLeft: '8px', fontSize: '0.85em' }}>{originalPrice}</span>,
            discountPercent > 0 && (
              <span key="badge" style={{
                marginLeft: '8px', fontSize: '0.75em', fontWeight: 600,
                color: '#ef4444', background: '#fef2f2', padding: '1px 6px',
                borderRadius: '4px',
              }}>{discountPercent}% OFF</span>
            )
          ]);
        }
      }
    }
  }

  // Render children
  const children = [];

  if (resolvedContent) {
    if (containsHTML(resolvedContent)) {
      return React.createElement(Tag, {
        ...props,
        dangerouslySetInnerHTML: { __html: resolvedContent }
      });
    }
    children.push(resolvedContent);
  }

  if (structure.children && structure.children.length > 0) {
    structure.children.forEach((child, index) => {
      children.push(
        <EditableElement
          key={index}
          structure={child}
          loopContext={loopContext}
          nodePath={`${nodePath}.children.${index}`}
          {...childProps}
        />
      );
    });
  }

  return React.createElement(Tag, props, children.length > 0 ? children : undefined);
});

EditableElement.displayName = 'EditableElement';

// ============================================================================
// SORTABLE SECTION (@dnd-kit wrapper for drag-and-drop)
// ============================================================================

const SortableSection = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isDragging ? 0.7 : 1,
    position: 'relative',
    zIndex: isDragging ? 999 : 'auto',
    boxShadow: isDragging ? '0 16px 48px rgba(0,0,0,0.15)' : 'none',
    borderRadius: isDragging ? '8px' : undefined,
    overflow: isDragging ? 'hidden' : undefined,
  };
  // Strip role="button" and tabIndex from attributes — they prevent
  // contentEditable children from receiving keyboard input.
  // Drag is handle-based (listeners go to the grip icon), so the
  // wrapper itself doesn't need to be a focusable button.
  const { role, tabIndex, ...safeAttributes } = attributes;
  return (
    <div ref={setNodeRef} style={style} {...safeAttributes}>
      {React.cloneElement(children, { dragListeners: listeners })}
    </div>
  );
};

// ============================================================================
// SECTION WRAPPER (wraps each block with controls)
// ============================================================================

const SectionWrapper = ({
  block, index, children,
  onToggleVisibility, onMoveUp, onMoveDown, onDuplicate, onDelete,
  isFirst, isLast, editMode,
  dragListeners,
  previewDevice = 'desktop'
}) => {
  const [hovered, setHovered] = useState(false);
  const [animPlayed, setAnimPlayed] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  // Animation config from block
  const anim = block.animation || {};
  const entranceType = anim.entrance || anim.type || 'none';
  const hasEntrance = entranceType !== 'none';
  const hoverType = anim.hover || 'none';
  const hasHover = hoverType !== 'none';
  const isScrollTriggered = !!anim.scrollTrigger;

  // IntersectionObserver for scroll-triggered animations
  useEffect(() => {
    if (!hasEntrance || !isScrollTriggered || !sectionRef.current) return;
    const threshold = parseFloat(anim.scrollThreshold) || 0.1;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animPlayed) {
          setIsInView(true);
          setAnimPlayed(true);
        }
      },
      { threshold }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasEntrance, isScrollTriggered, anim.scrollThreshold, animPlayed]);

  // Non-scroll-triggered: play immediately
  useEffect(() => {
    if (hasEntrance && !isScrollTriggered) {
      setIsInView(true);
      setAnimPlayed(true);
    }
  }, [hasEntrance, isScrollTriggered]);

  if (!editMode) {
    // In preview mode, still apply animation wrapper
    const keyframeName = ANIM_KEYFRAME_MAP[entranceType];
    const shouldAnimate = hasEntrance && keyframeName && isInView;
    const waitingForScroll = hasEntrance && isScrollTriggered && !isInView;

    const animStyle = {};
    if (shouldAnimate) {
      animStyle.animation = `${keyframeName} ${anim.duration || '0.8'}s ${anim.easing || 'ease-out'} ${anim.delay || '0'}s both`;
      if (anim.loop) {
        animStyle.animationIterationCount = anim.loopCount || 'infinite';
      }
    }
    if (waitingForScroll) {
      animStyle.opacity = 0;
    }
    // Hover transition
    if (hasHover) {
      animStyle.transition = `all ${anim.duration || '0.3'}s ${anim.easing || 'ease-out'}`;
    }

    const hoverStyles = (hasHover && hovered) ? (HOVER_STYLE_MAP[hoverType] || {}) : {};

    return (
      <div
        ref={sectionRef}
        data-block-id={block.id || undefined}
        data-section-index={index}
        className="editor-section-animated"
        style={{ ...animStyle, ...hoverStyles }}
        onMouseEnter={hasHover ? () => setHovered(true) : undefined}
        onMouseLeave={hasHover ? () => setHovered(false) : undefined}
      >
        {children}
      </div>
    );
  }

  const sectionLabel = block.id
    ? block.id.charAt(0).toUpperCase() + block.id.slice(1).replace(/[-_]/g, ' ')
    : block.type || ('Section ' + (index + 1));

  const isCompactDevice = previewDevice === 'mobile' || previewDevice === 'tablet';

  // Build animation style for edit mode
  const keyframeName = ANIM_KEYFRAME_MAP[entranceType];
  const shouldAnimate = hasEntrance && keyframeName && isInView;
  const waitingForScroll = hasEntrance && isScrollTriggered && !isInView;

  const wrapperAnimStyle = { marginTop: index === 0 ? 0 : undefined };
  if (shouldAnimate) {
    wrapperAnimStyle.animation = `${keyframeName} ${anim.duration || '0.8'}s ${anim.easing || 'ease-out'} ${anim.delay || '0'}s both`;
    if (anim.loop) {
      wrapperAnimStyle.animationIterationCount = anim.loopCount || 'infinite';
    }
  }
  if (waitingForScroll) {
    wrapperAnimStyle.opacity = 0;
  }
  // Hover effect from animation config (combined with existing section hover)
  if (hasHover && hovered) {
    const hoverStyles = HOVER_STYLE_MAP[hoverType] || {};
    Object.assign(wrapperAnimStyle, hoverStyles);
  }
  if (hasHover) {
    wrapperAnimStyle.transition = `all ${anim.duration || '0.3'}s ${anim.easing || 'ease-out'}`;
  }

  return (
    <div
      ref={sectionRef}
      data-block-id={block.id || undefined}
      data-section-index={index}
      className={'relative transition-all duration-300 editor-section-animated ' + (!block.enabled ? 'opacity-40 ' : '')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={wrapperAnimStyle}
    >
      {/* Section controls — top bar on mobile/tablet, left pill on desktop */}
      {isCompactDevice ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px',
            background: hovered ? 'rgba(15, 23, 42, 0.95)' : 'rgba(15, 23, 42, 0.7)',
            color: '#fff',
            transition: 'all 0.2s ease',
            opacity: hovered ? 1 : 0.4,
            zIndex: 30,
            position: 'relative',
            gap: '4px',
            flexShrink: 0,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            {...(dragListeners || {})}
            style={{ padding: '2px 4px', cursor: 'grab', color: '#fff', display: 'flex', alignItems: 'center' }}
            title="Drag to reorder"
          >
            <GripVertical style={{ width: '11px', height: '11px' }} />
          </div>
          <span style={{ fontSize: '9px', fontWeight: 600, maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2, color: '#fff' }}>
            {sectionLabel}
          </span>
          {!isFirst && (
            <button
              onClick={() => onMoveUp(index)}
              style={{ padding: '6px', minWidth: '28px', minHeight: '28px', color: 'rgba(255,255,255,0.85)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
              title="Move up"
            >
              <ChevronUp style={{ width: '13px', height: '13px' }} />
            </button>
          )}
          {!isLast && (
            <button
              onClick={() => onMoveDown(index)}
              style={{ padding: '6px', minWidth: '28px', minHeight: '28px', color: 'rgba(255,255,255,0.85)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
              title="Move down"
            >
              <ChevronDown style={{ width: '13px', height: '13px' }} />
            </button>
          )}
          <button
            onClick={() => onToggleVisibility(index)}
            style={{ padding: '6px', minWidth: '28px', minHeight: '28px', color: 'rgba(255,255,255,0.85)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
            title={block.enabled ? 'Hide section' : 'Show section'}
          >
            {block.enabled ? <Eye style={{ width: '13px', height: '13px' }} /> : <EyeOff style={{ width: '13px', height: '13px' }} />}
          </button>
          <button
            onClick={() => onDuplicate(index)}
            style={{ padding: '6px', minWidth: '28px', minHeight: '28px', color: 'rgba(255,255,255,0.85)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
            title="Duplicate section"
          >
            <Copy style={{ width: '13px', height: '13px' }} />
          </button>
          <button
            onClick={() => onDelete(index)}
            style={{ padding: '6px', minWidth: '28px', minHeight: '28px', color: 'rgba(255,255,255,0.85)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
            title="Delete section"
          >
            <Trash2 style={{ width: '13px', height: '13px' }} />
          </button>
        </div>
      ) : (
        <div
          className={'absolute -left-1 top-3 z-30 flex items-center gap-0.5 transition-all duration-300 ' +
            (hovered ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 -translate-x-3 pointer-events-none')}
        >
          <div style={{
            background: 'rgba(15, 23, 42, 0.95)', color: '#fff',
            borderRadius: '0 12px 12px 0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.06) inset',
            backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center',
          }}>
            {/* Drag handle */}
            <div
              {...(dragListeners || {})}
              style={{ padding: '5px 4px', cursor: 'grab', borderRight: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}
              title="Drag to reorder"
            >
              <GripVertical style={{ width: '12px', height: '12px' }} />
            </div>
            <span style={{
              fontSize: '10.5px', fontWeight: 600, padding: '5px 8px',
              maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              lineHeight: 1.2, color: '#e2e8f0', letterSpacing: '0.01em',
            }}>
              {sectionLabel}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
              {!isFirst && (
                <SectionCtrlBtn onClick={() => onMoveUp(index)} title="Move up">
                  <ChevronUp style={{ width: '12px', height: '12px' }} />
                </SectionCtrlBtn>
              )}
              {!isLast && (
                <SectionCtrlBtn onClick={() => onMoveDown(index)} title="Move down">
                  <ChevronDown style={{ width: '12px', height: '12px' }} />
                </SectionCtrlBtn>
              )}
              <SectionCtrlBtn onClick={() => onToggleVisibility(index)} title={block.enabled ? 'Hide section' : 'Show section'}>
                {block.enabled ? <Eye style={{ width: '12px', height: '12px' }} /> : <EyeOff style={{ width: '12px', height: '12px' }} />}
              </SectionCtrlBtn>
              <SectionCtrlBtn onClick={() => onDuplicate(index)} title="Duplicate section">
                <Copy style={{ width: '12px', height: '12px' }} />
              </SectionCtrlBtn>
              <SectionCtrlBtn onClick={() => onDelete(index)} title="Delete section" isDanger>
                <Trash2 style={{ width: '12px', height: '12px' }} />
              </SectionCtrlBtn>
            </div>
          </div>
        </div>
      )}

      {/* Subtle left border indicator on hover (desktop only) */}
      <div style={{
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        borderLeft: !isCompactDevice ? (hovered ? '2px solid #6366f1' : '2px solid transparent') : undefined,
        boxShadow: hovered && !isCompactDevice ? 'inset 0 0 0 1px rgba(99,102,241,0.05)' : 'none',
      }}>
        {children}
      </div>
    </div>
  );
};

/* Helper: Section control button with hover state */
const SectionCtrlBtn = ({ onClick, title, children, isDanger }) => {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        padding: '5px', color: h ? '#fff' : 'rgba(255,255,255,0.55)',
        background: h ? (isDanger ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)') : 'none',
        border: 'none', cursor: 'pointer', borderRadius: '4px',
        transition: 'all 0.15s ease', display: 'flex', alignItems: 'center',
      }}
    >
      {children}
    </button>
  );
};

// ============================================================================
// ADD SECTION BUTTON (shown between sections)
// ============================================================================

const AddSectionButton = ({ onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '2px 0', transition: 'padding 0.2s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Horizontal line */}
      <div style={{
        position: 'absolute', left: '20%', right: '20%', height: '1px',
        transition: 'all 0.3s ease',
        background: hovered ? 'linear-gradient(90deg, transparent, rgba(99,102,241,0.3), transparent)' : 'transparent',
      }} />
      <button
        onClick={onClick}
        style={{
          position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '5px',
          padding: '5px 14px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          border: hovered ? '1px solid #6366f1' : '1px solid #e5e7eb',
          cursor: 'pointer',
          background: hovered ? '#6366f1' : '#ffffff',
          color: hovered ? '#fff' : '#a1a1aa',
          boxShadow: hovered ? '0 4px 16px rgba(99,102,241,0.3)' : '0 1px 2px rgba(0,0,0,0.04)',
          transform: hovered ? 'scale(1.04)' : 'scale(1)',
          letterSpacing: '0.01em',
        }}
        title="Add section here"
      >
        <PlusCircle style={{ width: '13px', height: '13px' }} />
        <span>Add Section</span>
      </button>
    </div>
  );
};

// ============================================================================
// MAIN EDITABLE RENDERER
// ============================================================================

const EditableRenderer = ({
  templateJSON,
  onContentChange,
  onImageReplace,
  onBlockReorder,
  onBlockToggle,
  onBlockDuplicate,
  onBlockDelete,
  onBlockAdd,
  editMode = true,
  previewDevice = 'desktop',
  imageBaseUrl = '',
  onElementSelect,
  selectedNodePath,
  editingNodePath,
  onElementClick,
  onElementDoubleClick,
  onOpenConfigPanel,
  navbarData,   // NavbarWrapper attributes for persistent navbar display
  footerData,   // FooterWrapper attributes for persistent footer display
}) => {
  // Build data context
  const dataContext = useMemo(() => {
    if (!templateJSON?.content) return {};
    return {
      ...templateJSON.content,
      content: templateJSON.content,
      brand: templateJSON.content?.brand || {},
      theme: templateJSON.theme || {}
    };
  }, [templateJSON?.content, templateJSON?.theme]);

  // Dynamically load Google Fonts when theme typography changes
  useEffect(() => {
    const typography = templateJSON?.theme?.typography;
    if (!typography) return;
    const genericFonts = new Set(['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui']);
    const fonts = [typography.heading, typography.body, typography.primary, typography.secondary]
      .filter(f => f && !genericFonts.has(f));
    const uniqueFonts = [...new Set(fonts)];
    if (uniqueFonts.length === 0) return;

    // Remove previous dynamic font links
    document.querySelectorAll('link[data-theme-font]').forEach(el => el.remove());

    const families = uniqueFonts.map(f => f.replace(/\s+/g, '+')).join('&family=');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${families}:wght@300;400;500;600;700;800;900&display=swap`;
    link.setAttribute('data-theme-font', 'true');
    document.head.appendChild(link);

    return () => {
      document.querySelectorAll('link[data-theme-font]').forEach(el => el.remove());
    };
  }, [templateJSON?.theme?.typography]);

  // Apply theme CSS variables - comprehensive mapping
  const themeStyle = useMemo(() => {
    const theme = templateJSON?.theme || {};
    const colors = theme.colors || {};
    const typography = theme.typography || {};
    const vars = {};

    // Primary color (can be array or string)
    const prim = colors.primary
      ? (Array.isArray(colors.primary) ? colors.primary[0] : colors.primary)
      : null;
    if (prim) {
      vars['--color-primary'] = prim;
      vars['--color-primary-light'] = lightenColor(prim, 0.15);
      // Auto-derive text-on-primary: use white for dark primaries, dark for light primaries
      const r = parseInt(prim.slice(1, 3), 16) || 0;
      const g = parseInt(prim.slice(3, 5), 16) || 0;
      const b = parseInt(prim.slice(5, 7), 16) || 0;
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      vars['--color-text-on-primary'] = luminance > 0.5 ? '#111827' : '#ffffff';
    }
    if (colors.secondary) vars['--color-secondary'] = colors.secondary;

    // Accent color — check explicit accent, or derive from palette colors (amber, rose, etc.)
    if (colors.accent) {
      vars['--color-accent'] = colors.accent;
    } else {
      // Auto-derive accent from palette sub-objects
      const palettes = ['amber', 'rose', 'orange', 'emerald', 'teal', 'blue', 'indigo', 'violet'];
      for (const pal of palettes) {
        if (colors[pal] && typeof colors[pal] === 'object') {
          vars['--color-accent'] = colors[pal]['900'] || colors[pal]['800'] || colors[pal]['700'];
          vars['--color-accent-light'] = colors[pal]['700'] || colors[pal]['600'] || vars['--color-accent'];
          break;
        }
      }
    }
    if (colors.accentLight) vars['--color-accent-light'] = colors.accentLight;

    // Background (can be object or string)
    if (colors.background) {
      if (typeof colors.background === 'object') {
        vars['--color-background'] = colors.background.primary || '#ffffff';
        if (colors.background.secondary) vars['--color-background-secondary'] = colors.background.secondary;
      } else {
        vars['--color-background'] = colors.background;
      }
    }

    // Text (can be object or string) + textSecondary/textLight at top level
    if (colors.text) {
      if (typeof colors.text === 'object') {
        vars['--color-text'] = colors.text.primary || '#111111';
        vars['--color-text-secondary'] = colors.text.secondary || colors.textSecondary || '#666666';
        vars['--color-text-light'] = colors.text.light || colors.textLight || '#999999';
      } else {
        vars['--color-text'] = colors.text;
      }
    }
    // Top-level textSecondary/textLight (template 13 pattern)
    if (colors.textSecondary) vars['--color-text-secondary'] = colors.textSecondary;
    if (colors.textLight) vars['--color-text-light'] = colors.textLight;

    // Surface
    if (colors.surface) {
      vars['--color-surface'] = typeof colors.surface === 'object' ? colors.surface.primary || '#ffffff' : colors.surface;
    }

    // Border
    if (colors.border) {
      vars['--color-border'] = typeof colors.border === 'object' ? colors.border.primary || '#e5e5e5' : colors.border;
    }

    // Gray scale
    if (colors.gray && typeof colors.gray === 'object') {
      Object.entries(colors.gray).forEach(([shade, value]) => {
        vars[`--color-gray-${shade}`] = value;
      });
    }

    // Success/Error/Warning
    if (colors.success) vars['--color-success'] = colors.success;
    if (colors.error) vars['--color-error'] = colors.error;
    if (colors.warning) vars['--color-warning'] = colors.warning;

    // Derived computed colors (matches customer-side themeUtils.ts getThemeStyles)
    const primary = vars['--color-primary'] || '#111827';
    const accent = vars['--color-accent'] || primary;
    const bg = vars['--color-background'] || '#ffffff';
    vars['--color-background-rgb'] = hexToRgb(bg);
    vars['--color-hover'] = mixHex(primary, '#000000', 0.8);
    vars['--color-primary-10'] = mixHex(primary, '#ffffff', 0.1);
    vars['--color-primary-20'] = mixHex(primary, '#ffffff', 0.2);
    if (!vars['--color-accent-light']) {
      vars['--color-accent-light'] = mixHex(accent, '#ffffff', 0.2);
    }

    // Typography — append fallback only if not already a generic family
    const GENERIC_FONTS = new Set(['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui']);
    const quotedFontVal = (f, fallback) => {
      if (GENERIC_FONTS.has(f)) return f;
      // Quote multi-word font names (matches customer-side quoteFontName)
      const quoted = f.includes(' ') && !f.startsWith('"') && !f.startsWith("'") ? `"${f}"` : f;
      return `${quoted}, ${fallback}`;
    };
    if (typography.heading) vars['--font-heading'] = quotedFontVal(typography.heading, 'serif');
    if (typography.body) vars['--font-body'] = quotedFontVal(typography.body, 'sans-serif');
    if (typography.primary) vars['--font-primary'] = quotedFontVal(typography.primary, 'sans-serif');
    if (typography.secondary) vars['--font-secondary'] = quotedFontVal(typography.secondary, 'serif');

    return vars;
  }, [templateJSON?.theme]);

  const handleMoveUp = useCallback((index) => {
    if (index > 0 && onBlockReorder) onBlockReorder(index, index - 1);
  }, [onBlockReorder]);

  const handleMoveDown = useCallback((index) => {
    if (onBlockReorder) onBlockReorder(index, index + 1);
  }, [onBlockReorder]);

  const handleToggle = useCallback((index) => {
    if (onBlockToggle) onBlockToggle(index);
  }, [onBlockToggle]);

  const handleDuplicate = useCallback((index) => {
    if (onBlockDuplicate) onBlockDuplicate(index);
  }, [onBlockDuplicate]);

  const handleDelete = useCallback((index) => {
    if (onBlockDelete) onBlockDelete(index);
  }, [onBlockDelete]);

  const handleAddSection = useCallback((afterIndex) => {
    if (onBlockAdd) onBlockAdd(afterIndex);
  }, [onBlockAdd]);

  const visibleBlocks = useMemo(() => {
    if (!templateJSON?.blocks) return [];
    return templateJSON.blocks.filter(block => {
      if (!block.enabled && !editMode) return false;
      return true;
    });
  }, [templateJSON?.blocks, editMode]);

  // @dnd-kit sensors and drag end handler
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragEndDnD = useCallback((event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = visibleBlocks.findIndex(b => (b.id || `block-${templateJSON.blocks.indexOf(b)}`) === active.id);
      const newIndex = visibleBlocks.findIndex(b => (b.id || `block-${templateJSON.blocks.indexOf(b)}`) === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onBlockReorder(oldIndex, newIndex);
      }
    }
  }, [visibleBlocks, onBlockReorder, templateJSON?.blocks]);

  if (!templateJSON || !templateJSON.blocks) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center text-gray-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-2" />
          <p className="text-lg font-medium">Nothing to show yet</p>
          <p className="text-sm mt-1">Choose a template from the gallery to start designing your website</p>
        </div>
      </div>
    );
  }

  // Persistent navbar/footer that wraps page content (not shown when editing navbar/footer standalone)
  const navbarPreview = navbarData ? (
    <div style={{ position: 'relative', cursor: editMode ? 'pointer' : 'default' }}
         onClick={editMode && onOpenConfigPanel ? () => onOpenConfigPanel('navbar') : undefined}>
      <NavbarPreview attributes={navbarData} editMode={false} themeColors={templateJSON?.theme?.colors} previewDevice={previewDevice} />
      {editMode && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,70,229,0.08)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span style={{
            padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
            color: '#4f46e5', background: 'rgba(255,255,255,0.9)', border: '1px solid #e0e7ff',
            opacity: 0.7, pointerEvents: 'none',
          }}>Click to edit navigation bar</span>
        </div>
      )}
    </div>
  ) : null;

  const footerPreview = footerData ? (
    <div style={{ position: 'relative', cursor: editMode ? 'pointer' : 'default' }}
         onClick={editMode && onOpenConfigPanel ? () => onOpenConfigPanel('footer') : undefined}>
      <FooterPreview attributes={footerData} editMode={false} themeColors={templateJSON?.theme?.colors} previewDevice={previewDevice} />
      {editMode && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '20px', background: 'transparent', transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,70,229,0.08)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span style={{
            padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
            color: '#4f46e5', background: 'rgba(255,255,255,0.9)', border: '1px solid #e0e7ff',
            opacity: 0.7, pointerEvents: 'none',
          }}>Click to edit footer</span>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div style={themeStyle} className={`editable-renderer ${previewDevice !== 'desktop' ? 'overflow-x-hidden' : ''}`}>
      <style>{THEME_COLOR_CSS}</style>
      <style>{ANIMATION_KEYFRAMES_CSS}</style>
      {navbarPreview}
      {editMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndDnD}>
          <SortableContext items={visibleBlocks.map((b, i) => b.id || `block-${i}`)} strategy={verticalListSortingStrategy}>
            {visibleBlocks.map((block, index) => {
              if (!block.structure) return null;
              const blockId = block.id || `block-${index}`;

              const blockContent = (
                <EditableElement
                  structure={block.structure}
                  data={dataContext}
                  loopContext={undefined}
                  onContentChange={onContentChange}
                  onImageReplace={onImageReplace}
                  editMode={editMode}
                  nodePath={`blocks.${index}.structure`}
                  imageBaseUrl={imageBaseUrl}
                  previewDevice={previewDevice}
                  onElementSelect={onElementSelect}
                  selectedNodePath={selectedNodePath}
                  editingNodePath={editingNodePath}
                  onElementClick={onElementClick}
                  onElementDoubleClick={onElementDoubleClick}
                  onOpenConfigPanel={onOpenConfigPanel}
                />
              );

              return (
                <React.Fragment key={blockId}>
                  <SortableSection id={blockId}>
                    <SectionWrapper
                      block={block}
                      index={index}
                      onToggleVisibility={handleToggle}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      onDuplicate={handleDuplicate}
                      onDelete={handleDelete}
                      isFirst={index === 0}
                      isLast={index === visibleBlocks.length - 1}
                      editMode={editMode}
                      previewDevice={previewDevice}
                    >
                      {blockContent}
                    </SectionWrapper>
                  </SortableSection>
                  {index < visibleBlocks.length - 1 && (
                    <AddSectionButton onClick={() => handleAddSection(index)} />
                  )}
                </React.Fragment>
              );
            })}
          </SortableContext>
        </DndContext>
      ) : (
        visibleBlocks.map((block, index) => {
          if (!block.structure) return null;

          const blockContent = (
            <EditableElement
              structure={block.structure}
              data={dataContext}
              loopContext={undefined}
              onContentChange={onContentChange}
              onImageReplace={onImageReplace}
              editMode={editMode}
              nodePath={`blocks.${index}.structure`}
              imageBaseUrl={imageBaseUrl}
              previewDevice={previewDevice}
              onElementSelect={onElementSelect}
              onOpenConfigPanel={onOpenConfigPanel}
            />
          );

          return (
            <SectionWrapper
              key={block.id || index}
              block={block}
              index={index}
              onToggleVisibility={() => {}}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
              onDuplicate={() => {}}
              onDelete={() => {}}
              isFirst={index === 0}
              isLast={index === visibleBlocks.length - 1}
              editMode={false}
              previewDevice={previewDevice}
            >
              {blockContent}
            </SectionWrapper>
          );
        })
      )}
      {footerPreview}
    </div>
  );
};

export default EditableRenderer;
export { InlineTextEditor, ImageOverlay, SectionWrapper, AddSectionButton, EditableElement, processClassName };
